/**
 * Control de Labores SSCC (2026-10-09). Cada supervisor de Mano de Obra arma el
 * sábado su plan de trabajo de la semana (SS.HH. y comedores, con pasadas) y lo
 * envía al coordinador para validarlo. El día anterior elige de la lista de
 * trabajadores activos a su personal y le da a cada uno su parte. Si alguien
 * falta, el coordinador lo reemplaza, reparte sus puntos o cancela su
 * asignación. El trabajador manda por WhatsApp (bot de labores) foto +
 * ubicación por pasada. (Cambios del 2026-10-21.)
 */
import {
  rec_API_BASE as API_BASE,
  rec_EstructuraPrincipal as EstructuraPrincipal,
  rec_Lo as claseEntrada,
  rec_Modal as Modal,
  rec_React as React,
  rec_Ro as clasePrimario,
  rec_api as api,
  rec_obtenerUsuario as obtenerUsuario,
  rec_puedeAccion as puedeAccion,
  rec_zo as claseSecundario,
  rec_EA as XLSX,
  rec_AccionAsync as AccionAsync,
} from "../../runtime/index.jsx";
import "./labores.css";
import { MapaPuntos, VistaGeneral } from "./VistaGeneral.jsx";

// runtime/index.jsx importa este archivo antes de definir React (import circular).
const useEffect = (...a) => React.useEffect(...a);
const useState = (...a) => React.useState(...a);
const useCallback = (...a) => React.useCallback(...a);

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, `0`)}-${String(d.getDate()).padStart(2, `0`)}`;
};
const hora = (v) => (v ? new Date(v).toLocaleTimeString(`es-PE`, { hour: `2-digit`, minute: `2-digit` }) : `—`);
const sumarDias = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, `0`)}-${String(d.getDate()).padStart(2, `0`)}`;
};
const lunesDe = (iso) => sumarDias(iso, -((new Date(`${iso}T12:00:00`).getDay() + 6) % 7));
const diaCorto = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(`es-PE`, { weekday: `short`, day: `numeric` });
const ORIGEN = {
  PLAN: `Según su plan de trabajo`,
  PLAN_AJUSTADO: `Cambió la programación`,
  PLAN_AUTO: `Planificación (se aplicó sola)`,
  REEMPLAZO: `Reemplazo`,
  MANUAL: `Sin plan de trabajo`,
};
const diaLargo = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(`es-PE`, { weekday: `long`, day: `numeric`, month: `long` });
const ESTADO_PLAN = {
  BORRADOR: { texto: `Borrador`, clase: `lb-estado` },
  ENVIADO: { texto: `Por validar`, clase: `lb-estado lb-estado-enviado` },
  VALIDADO: { texto: `Validado`, clase: `lb-estado lb-estado-validado` },
  OBSERVADO: { texto: `Con observación`, clase: `lb-estado lb-estado-observado` },
};
const sumaPasadas = (lista) => lista.reduce((s, p) => s + p.pasadas, 0);

const PESTANAS = [
  { id: `general`, texto: `Vista general` },
  { id: `hoy`, texto: `Avance del día` },
  { id: `asignar`, texto: `Asignar personal` },
  { id: `plan`, texto: `Plan de trabajo` },
  { id: `trabajadores`, texto: `Trabajadores` },
  { id: `puntos`, texto: `Puntos` },
  { id: `registro`, texto: `Registro` },
];

export function ControlLabores() {
  const verReporte = puedeAccion(`labores.reporte.ver`);
  const [pestana, setPestana] = useState(verReporte ? `general` : `hoy`);
  const [fecha, setFecha] = useState(hoyISO());
  const [dia, setDia] = useState(null);
  const [trabajadores, setTrabajadores] = useState([]);
  const [puntos, setPuntos] = useState([]);
  const [error, setError] = useState(``);

  const cargar = useCallback(async () => {
    setError(``);
    try {
      const [d, t, p] = await Promise.all([
        api(`/labores/dia?fecha=${fecha}`, { sinCache: true }),
        api(`/labores/trabajadores`, { sinCache: true }),
        api(`/labores/puntos`, { sinCache: true }),
      ]);
      setDia(d);
      setTrabajadores(t);
      setPuntos(p);
    } catch (e) {
      setError(e.message);
    }
  }, [fecha]);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 30000);
    return () => clearInterval(t);
  }, [cargar]);

  const puedeAsignar = puedeAccion(`labores.asignaciones.gestionar`);
  const puedeMaestros = puedeAccion(`labores.maestros.gestionar`);
  // El coordinador valida el plan de trabajo y cubre las faltas del día.
  const coordinador = puedeAccion(`labores.planificacion.gestionar`);
  const opera = puedeAsignar || puedeMaestros || coordinador || puedeAccion(`labores.control.ver`);
  // El jefe de SSCC sólo ve la vista general y el avance; quien opera ve todo.
  const visibles = PESTANAS.filter((p) => (
    p.id === `general` || p.id === `registro` ? verReporte
      : p.id === `hoy` ? true
      : p.id === `asignar` ? puedeAsignar || coordinador
      : opera));

  return (
    <EstructuraPrincipal title="Control de Labores" accionesRapidas={false}>
    <div className="lb-pagina">
      <div className="lb-cabecera">
        <p className="lb-intro">Limpieza de SS.HH. y comedores: el plan de trabajo de la semana, quién va a dónde cada día, cuántas pasadas y la evidencia que llega por WhatsApp.</p>
        {pestana !== `plan` && pestana !== `trabajadores` && pestana !== `puntos` && pestana !== `registro` && (
          <div className="lb-fecha-caja">
            <div className="lb-filtros">
              <button className={fecha === hoyISO() ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => setFecha(hoyISO())}>Hoy</button>
              <button className={fecha === sumarDias(hoyISO(), 1) ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => setFecha(sumarDias(hoyISO(), 1))}>Mañana</button>
            </div>
            <label className="lb-fecha">
              <span>Día</span>
              <input type="date" className={claseEntrada} value={fecha} onChange={(e) => e.target.value && setFecha(e.target.value)} />
            </label>
          </div>
        )}
      </div>
      <nav className="lb-pestanas" role="tablist">
        {visibles.map((p) => (
          <button key={p.id} role="tab" aria-selected={pestana === p.id} className={pestana === p.id ? `lb-pestana lb-activa` : `lb-pestana`}
            onClick={() => { setPestana(p.id); if (p.id === `asignar` && fecha === hoyISO()) setFecha(sumarDias(hoyISO(), 1)); }}>
            {p.texto}
          </button>
        ))}
      </nav>
      {error && <p className="lb-error">{error}</p>}
      {pestana === `general` && <VistaGeneral dia={dia} puntos={puntos} fecha={fecha} />}
      {pestana === `hoy` && <Avance dia={dia} fecha={fecha} puntos={puntos} trabajadores={trabajadores} onCambio={cargar} puedeAsignar={puedeAsignar} coordinador={coordinador} />}
      {pestana === `asignar` && <Asignar dia={dia} fecha={fecha} trabajadores={trabajadores} puntos={puntos} onCambio={cargar} puede={puedeAsignar} coordinador={coordinador} maestros={puedeMaestros} />}
      {pestana === `trabajadores` && <Trabajadores lista={trabajadores} onCambio={cargar} puede={puedeMaestros} />}
      {pestana === `puntos` && <Puntos lista={puntos} onCambio={cargar} puede={puedeMaestros} />}
      {pestana === `plan` && <PlanDeTrabajo puntos={puntos} puede={puedeAsignar} coordinador={coordinador} />}
      {pestana === `registro` && <RegistroEvidencias trabajadores={trabajadores} />}
    </div>
    </EstructuraPrincipal>
  );
}

// ── Avance del día ──

/**
 * Avance del día (rehecho el 2026-10-31 para el piloto): con decenas de
 * trabajadores, una tarjeta por persona era una sábana. Ahora se agrupa por
 * supervisor o por fundo, cada grupo compara lo programado en el plan de
 * trabajo con lo asignado y lo hecho, y los problemas salen primero. Las fotos
 * se abren por fila.
 */
const FILTROS_ESTADO = [
  { id: `todos`, texto: `Todos` },
  { id: `pendientes`, texto: `Con pendientes` },
  { id: `sin`, texto: `Sin empezar` },
  { id: `fuera`, texto: `Fuera del punto` },
  { id: `completos`, texto: `Completos` },
];
const pctDe = (h, p) => (p ? Math.round((100 * h) / p) : 0);
const estadoDe = (hechas, pedidas) => (hechas >= pedidas ? `completo` : hechas > 0 ? `parcial` : `pendiente`);
const ultimaDe = (lista) => lista.reduce((u, x) => (x && (!u || x > u) ? x : u), null);

function Avance({ dia, fecha, puntos, trabajadores, onCambio, puedeAsignar, coordinador }) {
  const yo = obtenerUsuario()?.id;
  // El supervisor ve de entrada a su gente; el coordinador y el jefe, a todos.
  const veTodo = coordinador || puedeAccion(`labores.reporte.ver`);
  const [falta, setFalta] = useState(null);
  const [agrupar, setAgrupar] = useState(`supervisor`);
  const [supervisor, setSupervisor] = useState(veTodo ? `` : String(yo ?? ``));
  const [fundo, setFundo] = useState(``);
  const [estado, setEstado] = useState(`todos`);
  const [buscar, setBuscar] = useState(``);
  const [abiertos, setAbiertos] = useState(null);
  if (!dia) return <p className="lb-vacio">Cargando…</p>;

  const activas = dia.asignaciones.filter((a) => a.estado === `ACTIVA`);
  const editable = coordinador && fecha >= hoyISO();
  const radio = (id) => puntos.find((x) => x.id === id)?.radioM;
  const supervisores = new Map();
  for (const a of dia.asignaciones) if (a.supervisorId) supervisores.set(a.supervisorId, a.supervisor ?? `—`);
  for (const p of dia.programacion ?? []) supervisores.set(p.supervisorId, p.supervisor ?? `—`);
  const fundos = [...new Set(puntos.map((p) => p.fundo).filter(Boolean))].sort();
  const texto = buscar.trim().toLowerCase();

  // Una fila por punto asignado; los filtros se aplican a estas filas.
  const filas = [];
  for (const a of activas) {
    for (const ap of a.puntos) {
      const r = radio(ap.puntoId);
      const hechas = Math.min(ap.pasadas, ap.evidencias.length);
      filas.push({
        a, ap, hechas,
        fundo: ap.fundo ?? `—`,
        fuera: ap.evidencias.filter((e) => e.distanciaM != null && r != null && e.distanciaM > r).length,
        ultima: ultimaDe(ap.evidencias.map((e) => e.tomadaEn)),
      });
    }
  }
  const pasaFiltro = (f) => (!supervisor || String(f.a.supervisorId) === supervisor)
    && (!fundo || f.fundo === fundo)
    && (!texto || `${f.a.trabajador} ${f.a.telefono ?? ``} ${f.ap.codigo} ${f.ap.nombre}`.toLowerCase().includes(texto))
    && (estado === `todos`
      || (estado === `pendientes` && f.hechas < f.ap.pasadas)
      || (estado === `sin` && f.hechas === 0)
      || (estado === `fuera` && f.fuera > 0)
      || (estado === `completos` && f.hechas >= f.ap.pasadas));
  const vis = filas.filter(pasaFiltro);

  // Lo programado (plan de trabajo) contra lo asignado, por supervisor y punto.
  const filtraPlan = estado === `todos` && !texto;
  const programado = [];
  for (const pr of dia.programacion ?? []) {
    if (supervisor && String(pr.supervisorId) !== supervisor) continue;
    for (const x of pr.puntos) {
      if (fundo && x.fundo !== fundo) continue;
      const asignadas = activas.filter((a) => a.supervisorId === pr.supervisorId)
        .reduce((s, a) => s + a.puntos.filter((ap) => ap.puntoId === x.puntoId).reduce((t, ap) => t + ap.pasadas, 0), 0);
      programado.push({ supervisorId: pr.supervisorId, estadoPlan: pr.estado, fundo: x.fundo ?? `—`, codigo: x.codigo, nombre: x.nombre, pasadas: x.pasadas, faltan: Math.max(0, x.pasadas - asignadas) });
    }
  }

  const clave = (f) => (agrupar === `supervisor` ? String(f.a.supervisorId ?? `—`) : f.fundo);
  const grupos = new Map();
  const grupo = (k, nombre) => {
    if (!grupos.has(k)) grupos.set(k, { k, nombre, filas: [], plan: [], estadoPlan: null });
    return grupos.get(k);
  };
  for (const f of vis) grupo(clave(f), agrupar === `supervisor` ? f.a.supervisor ?? `Sin supervisor` : f.fundo).filas.push(f);
  if (filtraPlan) {
    for (const x of programado) {
      const k = agrupar === `supervisor` ? String(x.supervisorId) : x.fundo;
      const g = grupo(k, agrupar === `supervisor` ? supervisores.get(x.supervisorId) : x.fundo);
      g.plan.push(x);
      if (agrupar === `supervisor`) g.estadoPlan = x.estadoPlan;
    }
  }
  const lista = [...grupos.values()].map((g) => {
    const pedidas = g.filas.reduce((s, f) => s + f.ap.pasadas, 0);
    const hechas = g.filas.reduce((s, f) => s + f.hechas, 0);
    return {
      ...g, pedidas, hechas,
      trabajadores: new Set(g.filas.map((f) => f.a.trabajadorId)).size,
      sinEmpezar: g.filas.filter((f) => f.hechas === 0).length,
      fuera: g.filas.reduce((s, f) => s + f.fuera, 0),
      programadas: g.plan.reduce((s, x) => s + x.pasadas, 0),
      sinAsignar: g.plan.filter((x) => x.faltan > 0),
      ultima: ultimaDe(g.filas.map((f) => f.ultima)),
    };
  }).sort((x, y) => (pctDe(x.hechas, x.pedidas) - pctDe(y.hechas, y.pedidas)) || x.nombre.localeCompare(y.nombre));

  const abiertosAhora = abiertos ?? new Set(lista.length <= 3 ? lista.map((g) => g.k) : []);
  const alternar = (k) => {
    const s = new Set(abiertosAhora);
    if (s.has(k)) s.delete(k); else s.add(k);
    setAbiertos(s);
  };

  const pedidas = vis.reduce((s, f) => s + f.ap.pasadas, 0);
  const hechas = vis.reduce((s, f) => s + f.hechas, 0);
  const fuera = vis.reduce((s, f) => s + f.fuera, 0);
  const sinAsignar = programado.reduce((s, x) => s + x.faltan, 0);
  const cambios = dia.asignaciones.filter((a) => a.estado !== `ACTIVA` && (!supervisor || String(a.supervisorId) === supervisor));

  return (
    <div className="lb-avance">
      <div className="lb-resumen">
        <Cifra valor={`${pctDe(hechas, pedidas)}%`} etiqueta={`avance (${hechas} de ${pedidas} pasadas)`} barra={pctDe(hechas, pedidas)} />
        <Cifra valor={new Set(vis.map((f) => f.a.trabajadorId)).size} etiqueta="trabajadores en campo" />
        <Cifra valor={vis.filter((f) => f.hechas === 0).length} etiqueta="puntos sin ninguna pasada" alerta={fecha < hoyISO() && vis.some((f) => f.hechas === 0)} />
        <Cifra valor={fuera} etiqueta="evidencias fuera del punto" alerta={fuera > 0} />
        {filtraPlan && <Cifra valor={sinAsignar} etiqueta="pasadas del plan sin nadie asignado" alerta={sinAsignar > 0} />}
        {cambios.length > 0 && <Cifra valor={cambios.length} etiqueta="faltas cubiertas" />}
      </div>

      <div className="lb-barra-filtros">
        <div className="lb-filtros" role="group" aria-label="Agrupar por">
          <span className="lb-sub">Agrupar por</span>
          {[[`supervisor`, `Supervisor`], [`fundo`, `Fundo`]].map(([id, t]) => (
            <button key={id} className={agrupar === id ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => { setAgrupar(id); setAbiertos(null); }}>{t}</button>
          ))}
        </div>
        <select className={`${claseEntrada} lb-selector`} value={supervisor} onChange={(e) => { setSupervisor(e.target.value); setAbiertos(null); }} aria-label="Supervisor">
          <option value="">Todos los supervisores</option>
          {[...supervisores].sort((x, y) => x[1].localeCompare(y[1])).map(([id, n]) => <option key={id} value={id}>{id === yo ? `${n} (yo)` : n}</option>)}
        </select>
        <select className={`${claseEntrada} lb-selector`} value={fundo} onChange={(e) => { setFundo(e.target.value); setAbiertos(null); }} aria-label="Fundo">
          <option value="">Todos los fundos</option>
          {fundos.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <input className={`${claseEntrada} lb-buscar`} value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar trabajador, celular o punto" />
      </div>
      <div className="lb-barra-filtros">
        <div className="lb-filtros">
          {FILTROS_ESTADO.map((x) => (
            <button key={x.id} className={estado === x.id ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => setEstado(x.id)}>{x.texto}</button>
          ))}
        </div>
        {lista.length > 1 && (
          <div className="lb-filtros">
            <button className="lb-filtro" onClick={() => setAbiertos(new Set(lista.map((g) => g.k)))}>Abrir todos</button>
            <button className="lb-filtro" onClick={() => setAbiertos(new Set())}>Cerrar todos</button>
          </div>
        )}
      </div>

      {activas.length === 0 && !(dia.programacion ?? []).length ? (
        <p className="lb-vacio">Nadie asignado para el {diaLargo(fecha)}. Cada supervisor elige a su personal en «Asignar personal».</p>
      ) : lista.length === 0 ? (
        <p className="lb-vacio">Nada coincide con los filtros.</p>
      ) : (
        <div className="lb-grupos">
          {lista.map((g) => (
            <GrupoAvance key={g.k} g={g} agrupar={agrupar} abierto={abiertosAhora.has(g.k)} onAlternar={() => alternar(g.k)}
              dia={dia} puntos={puntos} onCambio={onCambio} puedeAsignar={puedeAsignar} onFalta={editable ? setFalta : null} />
          ))}
        </div>
      )}
      {cambios.length > 0 && <CambiosDelDia cambios={cambios} dia={dia} />}
      {dia.sinPunto?.length > 0 && veTodo && (
        <section className="lb-bloque">
          <h3>Evidencias sin punto identificado</h3>
          <div className="lb-fotos">{dia.sinPunto.map((e) => <Foto key={e.id} e={e} />)}</div>
        </section>
      )}
      {falta && <ModalFalta a={falta} dia={dia} trabajadores={trabajadores} onClose={() => setFalta(null)} onListo={() => { setFalta(null); onCambio(); }} />}
    </div>
  );
}

/** Un supervisor o un fundo: cabecera con plan, asignado y hecho; dentro, una fila por trabajador o por punto. */
function GrupoAvance({ g, agrupar, abierto, onAlternar, dia, puntos, onCambio, puedeAsignar, onFalta }) {
  const pct = pctDe(g.hechas, g.pedidas);
  const plan = g.estadoPlan ? ESTADO_PLAN[g.estadoPlan] : null;
  // Por supervisor, una fila por trabajador con sus puntos; por fundo, una fila por punto.
  const porTrabajador = new Map();
  for (const f of g.filas) {
    const r = porTrabajador.get(f.a.id) ?? { a: f.a, filas: [] };
    r.filas.push(f);
    porTrabajador.set(f.a.id, r);
  }
  const trabajadores = [...porTrabajador.values()].map((t) => ({
    ...t,
    pedidas: t.filas.reduce((s, f) => s + f.ap.pasadas, 0),
    hechas: t.filas.reduce((s, f) => s + f.hechas, 0),
    fuera: t.filas.reduce((s, f) => s + f.fuera, 0),
    ultima: ultimaDe(t.filas.map((f) => f.ultima)),
  })).sort((x, y) => pctDe(x.hechas, x.pedidas) - pctDe(y.hechas, y.pedidas) || x.a.trabajador.localeCompare(y.a.trabajador));
  const porPunto = [...g.filas].sort((x, y) => pctDe(x.hechas, x.ap.pasadas) - pctDe(y.hechas, y.ap.pasadas) || x.ap.codigo.localeCompare(y.ap.codigo));
  const reemplazaA = (a) => (a.reemplazoDe ? dia.asignaciones.find((x) => x.id === a.reemplazoDe)?.trabajador : null);

  return (
    <section className={abierto ? `lb-grupo-avance lb-grupo-abierto` : `lb-grupo-avance`}>
      <button type="button" className="lb-grupo-cab" onClick={onAlternar} aria-expanded={abierto}>
        <span className="lb-grupo-flecha" aria-hidden="true">{abierto ? `▾` : `▸`}</span>
        <span className="lb-grupo-nombre">
          <strong>{g.nombre}</strong>
          {plan && <span className={plan.clase}>Plan {plan.texto.toLowerCase()}</span>}
          {agrupar === `supervisor` && g.plan.length === 0 && g.filas.length > 0 && <span className="lb-estado">Sin plan de trabajo</span>}
        </span>
        <span className="lb-grupo-datos">
          <span><b>{g.trabajadores}</b> trab.</span>
          {g.programadas > 0 && <span title="Pasadas programadas en el plan de trabajo"><b>{g.programadas}</b> en el plan</span>}
          <span><b>{g.hechas}/{g.pedidas}</b> pasadas</span>
          {g.sinEmpezar > 0 && <span className="lb-aviso"><b>{g.sinEmpezar}</b> sin empezar</span>}
          {g.fuera > 0 && <span className="lb-mal"><b>{g.fuera}</b> fuera</span>}
          {g.sinAsignar.length > 0 && <span className="lb-mal"><b>{g.sinAsignar.reduce((s, x) => s + x.faltan, 0)}</b> sin asignar</span>}
          <span className="lb-sub">última {hora(g.ultima)}</span>
        </span>
        <span className="lb-grupo-avance-barra"><Barra pct={pct} /></span>
      </button>
      {abierto && (
        <div className="lb-grupo-cuerpo">
          {g.sinAsignar.length > 0 && (
            <p className="lb-nota lb-nota-alerta">
              Programado sin nadie asignado: {g.sinAsignar.map((x) => `${x.codigo} × ${x.faltan}`).join(` · `)}
              {agrupar === `fundo` && <span className="lb-sub"> (según el plan de cada supervisor)</span>}
            </p>
          )}
          {g.filas.length === 0 ? (
            <p className="lb-sub">Tiene plan para este día, pero todavía no eligió a su personal.</p>
          ) : agrupar === `supervisor` ? (
            <div className="lb-tabla-caja">
              <table className="lb-tabla lb-tabla-avance">
                <thead><tr><th>Trabajador</th><th>Puntos</th><th>Avance</th><th>Última</th><th>Fuera</th><th>Lista</th><th /></tr></thead>
                <tbody>
                  {trabajadores.map((t) => (
                    <FilaTrabajador key={t.a.id} t={t} puntos={puntos} onCambio={onCambio} puedeAsignar={puedeAsignar} reemplazaA={reemplazaA(t.a)} onFalta={onFalta} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="lb-tabla-caja">
              <table className="lb-tabla lb-tabla-avance">
                <thead><tr><th>Punto</th><th>Trabajador</th><th>Supervisor</th><th>Pasadas</th><th>Última</th><th>Fuera</th><th /></tr></thead>
                <tbody>{porPunto.map((f) => <FilaPunto key={f.ap.id} f={f} puntos={puntos} />)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Barra({ pct }) {
  const color = pct >= 100 ? `var(--success)` : pct > 0 ? `var(--warning)` : `var(--border)`;
  return (
    <span className="lb-avance-celda">
      <span className="lb-barra"><i style={{ width: `${pct}%`, background: color }} /></span>
      <b>{pct}%</b>
    </span>
  );
}

const fueraDeLugar = (e, ap, puntos) => {
  const p = puntos.find((x) => x.id === ap.puntoId);
  return e.distanciaM != null && p && e.distanciaM > p.radioM;
};

function FilaTrabajador({ t, puntos, onCambio, puedeAsignar, reemplazaA, onFalta }) {
  const [fotos, setFotos] = useState(false);
  const { a } = t;
  const enviar = async () => {
    await api(`/labores/asignaciones/${a.id}/enviar`, { method: `POST` });
    onCambio();
  };
  const conFotos = t.filas.some((f) => f.ap.evidencias.length > 0);
  return (
    <>
      <tr>
        <td>
          <span className="lb-nombre">{a.trabajador}</span>
          <span className="lb-sub"><br />{a.telefono?.replace(/^51/, ``)}{reemplazaA ? ` · reemplaza a ${reemplazaA}` : ``}</span>
        </td>
        <td>
          <span className="lb-chips">
            {t.filas.map((f) => (
              <span key={f.ap.id} className={`lb-chip-punto lb-${estadoDe(f.hechas, f.ap.pasadas)}`} title={f.ap.nombre}>
                {f.ap.codigo} <b>{f.hechas}/{f.ap.pasadas}</b>
              </span>
            ))}
          </span>
        </td>
        <td><Barra pct={pctDe(t.hechas, t.pedidas)} /></td>
        <td>{hora(t.ultima)}</td>
        <td className={t.fuera ? `lb-mal` : ``}>{t.fuera}</td>
        <td>
          {a.enviadaEn ? <span className="lb-chip lb-ok">Enviada {hora(a.enviadaEn)}</span>
            : a.enviarYa ? <span className="lb-chip">Enviando…</span>
            : <span className="lb-chip">A las 6:00</span>}
        </td>
        <td className="lb-acciones-fila">
          {conFotos && <button className="lb-filtro" onClick={() => setFotos(!fotos)} aria-expanded={fotos}>{fotos ? `Ocultar fotos` : `Fotos`}</button>}
          {puedeAsignar && !a.enviarYa && <button className="lb-filtro" onClick={enviar}>{a.enviadaEn ? `Reenviar` : `Enviar ahora`}</button>}
          {onFalta && <button className="lb-filtro" onClick={() => onFalta(a)}>Faltó / cubrir</button>}
        </td>
      </tr>
      {fotos && (
        <tr className="lb-fila-detalle">
          <td colSpan={7}><PuntosDetalle aps={t.filas.map((f) => f.ap)} puntos={puntos} /></td>
        </tr>
      )}
    </>
  );
}

function FilaPunto({ f, puntos }) {
  const [fotos, setFotos] = useState(false);
  return (
    <>
      <tr>
        <td><span className="lb-codigo">{f.ap.codigo}</span> <span className="lb-sub">{f.ap.nombre}</span></td>
        <td>{f.a.trabajador}</td>
        <td className="lb-sub">{f.a.supervisor ?? `—`}</td>
        <td>
          <span className="lb-pasadas">
            {Array.from({ length: f.ap.pasadas }, (_, i) => <i key={i} className={i < f.hechas ? `lb-hecha` : ``} />)}
            <b>{f.hechas}/{f.ap.pasadas}</b>
          </span>
        </td>
        <td>{hora(f.ultima)}</td>
        <td className={f.fuera ? `lb-mal` : ``}>{f.fuera}</td>
        <td className="lb-acciones-fila">
          {f.ap.evidencias.length > 0 && <button className="lb-filtro" onClick={() => setFotos(!fotos)} aria-expanded={fotos}>{fotos ? `Ocultar fotos` : `Fotos`}</button>}
        </td>
      </tr>
      {fotos && (
        <tr className="lb-fila-detalle">
          <td colSpan={7}><PuntosDetalle aps={[f.ap]} puntos={puntos} /></td>
        </tr>
      )}
    </>
  );
}

/** Los puntos de una fila con sus pasadas y las fotos de cada evidencia. */
function PuntosDetalle({ aps, puntos }) {
  return (
    <ul className="lb-puntos">
      {aps.map((p) => {
        const n = Math.min(p.pasadas, p.evidencias.length);
        return (
          <li key={p.id} className={`lb-punto lb-${estadoDe(n, p.pasadas)}`}>
            <div className="lb-punto-cab">
              <span className="lb-codigo">{p.codigo}</span>
              <span className="lb-punto-nombre">{p.nombre}</span>
              <span className="lb-pasadas">
                {Array.from({ length: p.pasadas }, (_, i) => <i key={i} className={i < n ? `lb-hecha` : ``} />)}
                <b>{n}/{p.pasadas}</b>
              </span>
            </div>
            {p.evidencias.length > 0 && (
              <div className="lb-fotos">
                {p.evidencias.map((e) => <Foto key={e.id} e={e} fuera={fueraDeLugar(e, p, puntos)} />)}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Quién faltó y cómo se cubrió (reemplazo, reparto o cancelación). */
function CambiosDelDia({ cambios, dia }) {
  const que = (a) => {
    if (a.estado === `REEMPLAZADA`) {
      const r = dia.asignaciones.find((x) => x.reemplazoDe === a.id);
      return `Lo reemplazó ${r?.trabajador ?? `otra persona`}`;
    }
    return a.estado === `DISTRIBUIDA` ? `Sus puntos se repartieron entre sus compañeros` : `Se canceló su asignación`;
  };
  return (
    <section className="lb-bloque">
      <h3>Faltas y cambios del día</h3>
      <div className="lb-tabla-caja">
        <table className="lb-tabla">
          <thead><tr><th>Trabajador</th><th>Supervisor</th><th>Qué se hizo</th><th>Motivo</th><th>Cambió</th></tr></thead>
          <tbody>
            {cambios.map((a) => (
              <tr key={a.id}>
                <td>{a.trabajador}</td>
                <td>{a.supervisor ?? `—`}</td>
                <td>{que(a)}{a.estado === `CANCELADA` && a.puntos.length > 0 && <span className="lb-sub"><br />{a.puntos.map((p) => `${p.codigo}×${p.pasadas}`).join(` · `)}</span>}</td>
                <td>{a.motivo ?? `—`}</td>
                <td>{a.cambiadaPor ?? `—`}<span className="lb-sub"><br />{hora(a.cambiadaEn)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const MOTIVOS = [`Faltó`, `Pasó a otro supervisor`, `Otro`];

/**
 * Falta de un trabajador (coordinador): otra persona lo reemplaza, sus puntos
 * se reparten entre sus compañeros (p. ej. si pasó a otro supervisor) o se
 * cancela su asignación.
 */
function ModalFalta({ a, dia, trabajadores, onClose, onListo }) {
  const ocupados = new Set(dia.asignaciones.filter((x) => x.estado === `ACTIVA`).map((x) => x.trabajadorId));
  const libres = trabajadores.filter((t) => t.activo && !ocupados.has(t.id));
  const companeros = dia.asignaciones.filter((x) => x.estado === `ACTIVA` && x.id !== a.id && x.supervisorId === a.supervisorId);
  const otros = dia.asignaciones.filter((x) => x.estado === `ACTIVA` && x.id !== a.id && x.supervisorId !== a.supervisorId);
  const [accion, setAccion] = useState(`reemplazar`);
  const [motivo, setMotivo] = useState(MOTIVOS[0]);
  const [otroMotivo, setOtroMotivo] = useState(``);
  const [reemplazo, setReemplazo] = useState(``);
  const [buscar, setBuscar] = useState(``);
  const [reparto, setReparto] = useState(() => Object.fromEntries(a.puntos.map((p, i) => [p.id, companeros.length ? companeros[i % companeros.length].id : ``])));
  const [error, setError] = useState(``);
  const [guardando, setGuardando] = useState(false);
  const filtrados = libres.filter((t) => !buscar || `${t.nombre} ${t.dni ?? ``}`.toLowerCase().includes(buscar.toLowerCase()));

  const guardar = async () => {
    setError(``);
    setGuardando(true);
    const m = motivo === `Otro` ? otroMotivo : motivo;
    try {
      if (accion === `reemplazar`) {
        await api(`/labores/asignaciones/${a.id}/reemplazar`, { method: `POST`, body: JSON.stringify({ trabajadorId: reemplazo ? Number(reemplazo) : null, motivo: m }) });
      } else if (accion === `distribuir`) {
        const lista = Object.entries(reparto).map(([ap, dest]) => ({ asignacionPuntoId: Number(ap), asignacionId: dest ? Number(dest) : null }));
        await api(`/labores/asignaciones/${a.id}/distribuir`, { method: `POST`, body: JSON.stringify({ reparto: lista, motivo: m }) });
      } else {
        await api(`/labores/asignaciones/${a.id}/cancelar`, { method: `POST`, body: JSON.stringify({ motivo: m }) });
      }
      onListo();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const opciones = [
    { id: `reemplazar`, titulo: `Reemplazarlo`, texto: `Otra persona libre ese día hace todos sus puntos.` },
    { id: `distribuir`, titulo: `Repartir entre sus compañeros`, texto: `Cada punto pasa a un compañero; si ya lo tenía, se le suman las pasadas.` },
    { id: `cancelar`, titulo: `Cancelar su asignación`, texto: `Sus puntos no se hacen ese día y no cuentan en el cumplimiento.` },
  ];
  const puedeGuardar = accion === `reemplazar` ? !!reemplazo : accion === `distribuir` ? a.puntos.every((p) => reparto[p.id]) : true;

  return (
    <Modal titulo={`${a.trabajador} · cubrir su asignación`} onClose={onClose}>
      <div className="lb-form">
        <p className="lb-sub">Con {a.supervisor ?? `—`}: {a.puntos.map((p) => `${p.codigo} × ${p.pasadas}`).join(` · `)}</p>
        <div className="lb-grupo">
          <span className="lb-grupo-titulo">Motivo</span>
          <div className="lb-filtros">
            {MOTIVOS.map((m) => <button key={m} type="button" className={motivo === m ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => setMotivo(m)}>{m}</button>)}
          </div>
          {motivo === `Otro` && <input className={claseEntrada} value={otroMotivo} onChange={(e) => setOtroMotivo(e.target.value)} placeholder="Escribe el motivo" maxLength={200} />}
        </div>
        <div className="lb-acciones-falta">
          {opciones.map((o) => (
            <label key={o.id} className={accion === o.id ? `lb-opcion lb-opcion-on` : `lb-opcion`}>
              <input type="radio" name="accion-falta" checked={accion === o.id} onChange={() => setAccion(o.id)} />
              <span className="lb-opcion-nombre"><strong>{o.titulo}</strong><span className="lb-sub"><br />{o.texto}</span></span>
            </label>
          ))}
        </div>
        {accion === `reemplazar` && (
          <div className="lb-grupo">
            <span className="lb-grupo-titulo">Quién lo reemplaza ({libres.length} libres ese día)</span>
            <input className={claseEntrada} value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar por nombre o DNI" />
            <div className="lb-lista lb-lista-elegir">
              {filtrados.map((t) => (
                <label key={t.id} className={String(t.id) === reemplazo ? `lb-opcion lb-opcion-on` : `lb-opcion`}>
                  <input type="radio" name="reemplazo" checked={String(t.id) === reemplazo} onChange={() => setReemplazo(String(t.id))} />
                  <span className="lb-opcion-nombre">{t.nombre}<span className="lb-sub"> · DNI {t.dni ?? `—`}</span></span>
                </label>
              ))}
            </div>
            {libres.length === 0 && <p className="lb-sub">No hay trabajadores activos libres ese día.</p>}
          </div>
        )}
        {accion === `distribuir` && (
          <div className="lb-grupo">
            <span className="lb-grupo-titulo">A quién pasa cada punto</span>
            {companeros.length === 0 && otros.length === 0 && <p className="lb-sub">No hay nadie más trabajando ese día.</p>}
            {a.puntos.map((p) => (
              <div key={p.id} className="lb-reparto">
                <span className="lb-codigo">{p.codigo}</span>
                <span className="lb-opcion-nombre">{p.nombre} · {p.pasadas} {p.pasadas === 1 ? `pasada` : `pasadas`}</span>
                <select className={claseEntrada} value={reparto[p.id] ?? ``} onChange={(e) => setReparto((r) => ({ ...r, [p.id]: e.target.value }))} aria-label={`Quién hace ${p.codigo}`}>
                  <option value="">Elige</option>
                  {companeros.length > 0 && <optgroup label={`Compañeros (${a.supervisor ?? `—`})`}>{companeros.map((x) => <option key={x.id} value={x.id}>{x.trabajador}</option>)}</optgroup>}
                  {otros.length > 0 && <optgroup label="Con otros supervisores">{otros.map((x) => <option key={x.id} value={x.id}>{x.trabajador} · {x.supervisor}</option>)}</optgroup>}
                </select>
              </div>
            ))}
            {motivo === `Pasó a otro supervisor` && <p className="lb-sub">Al repartir sus puntos, {a.trabajador.split(` `)[0]} queda libre ese día y otro supervisor puede elegirlo.</p>}
          </div>
        )}
        {error && <p className="lb-error">{error}</p>}
        <div className="lb-botones">
          <button className={claseSecundario} onClick={onClose}>Cancelar</button>
          <button className={clasePrimario} disabled={guardando || !puedeGuardar} onClick={guardar}>{guardando ? `Guardando…` : `Guardar el cambio`}</button>
        </div>
      </div>
    </Modal>
  );
}

function Cifra({ valor, etiqueta, barra, alerta }) {
  return (
    <div className={alerta ? `lb-cifra lb-cifra-alerta` : `lb-cifra`}>
      <span className="lb-cifra-valor">{valor}</span>
      <span className="lb-cifra-etiqueta">{etiqueta}</span>
      {barra != null && <span className="lb-barra"><i style={{ width: `${barra}%` }} /></span>}
    </div>
  );
}


/** La foto viene del backend con el token (un <img> no manda la cabecera). */
function Foto({ e, fuera }) {
  const [url, setUrl] = useState(null);
  const [grande, setGrande] = useState(false);
  useEffect(() => {
    if (!e.tieneFoto) return;
    let vivo = true, u = null;
    fetch(`${API_BASE}/labores/evidencias/${e.id}/foto`, { headers: { Authorization: `Bearer ${localStorage.getItem(`gth_auth_token`) ?? ``}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b && vivo) { u = URL.createObjectURL(b); setUrl(u); } })
      .catch(() => {});
    return () => { vivo = false; if (u) URL.revokeObjectURL(u); };
  }, [e.id]);
  const atraso = e.recibidaEn && e.tomadaEn ? (new Date(e.recibidaEn) - new Date(e.tomadaEn)) / 60000 : 0;
  return (
    <figure className="lb-foto">
      <button type="button" onClick={() => url && setGrande(true)} aria-label="Ver foto">
        {url ? <img src={url} alt="Evidencia" /> : <span className="lb-sin-foto">{e.tieneFoto ? `…` : `Sin foto`}</span>}
      </button>
      <figcaption>
        <span>{hora(e.tomadaEn)}</span>
        {e.distanciaM == null ? <span className="lb-aviso">sin ubicación</span>
          : fuera ? <span className="lb-mal">a {e.distanciaM} m</span>
          : <span className="lb-bien">en el punto</span>}
        {e.identificado === `CODIGO` && <span>código escrito</span>}
        {e.identificado === `UNICO` && <span>sin código (único pendiente)</span>}
        {atraso > 15 && <span className="lb-aviso">llegó {atraso >= 60 ? `${Math.round(atraso / 60)} h` : `${Math.round(atraso)} min`} después</span>}
      </figcaption>
      {grande && (
        <Modal titulo={`Evidencia · ${hora(e.tomadaEn)}`} onClose={() => setGrande(false)}>
          <img src={url} alt="Evidencia" className="lb-foto-grande" />
          {e.lat != null && <p className="lb-sub">Ubicación: {e.lat.toFixed(5)}, {e.lng.toFixed(5)}</p>}
        </Modal>
      )}
    </figure>
  );
}

// ── Asignar personal (el día anterior, cada supervisor) ──

/**
 * El supervisor ve su programación del día (de su plan de trabajo) y elige de la
 * lista de trabajadores activos a quienes lleva, dándole a cada uno su parte. Si
 * la programación cambió, puede asignar otros puntos. El coordinador puede verlo
 * y armarlo en nombre de cualquier supervisor.
 */
function Asignar({ dia, fecha, trabajadores, puntos, onCambio, puede, coordinador, maestros }) {
  const yo = obtenerUsuario()?.id;
  const [supervisores, setSupervisores] = useState([]);
  const [elegido, setElegido] = useState(null);
  const [editando, setEditando] = useState(null);
  const [aviso, setAviso] = useState(``);
  const [buscar, setBuscar] = useState(``);
  const [verTodos, setVerTodos] = useState(false);
  useEffect(() => { if (coordinador) api(`/labores/supervisores`).then(setSupervisores).catch(() => {}); }, [coordinador]);
  if (!dia) return <p className="lb-vacio">Cargando…</p>;

  // El coordinador abre en el primer supervisor con programación ese día (si él no tiene).
  const porDefecto = (dia.programacion ?? []).some((p) => p.supervisorId === yo) ? yo : (dia.programacion?.[0]?.supervisorId ?? yo);
  const sup = coordinador ? (elegido ?? porDefecto) : yo;
  const supNombre = supervisores.find((s) => s.id === sup)?.nombre;
  const prog = (dia.programacion ?? []).find((p) => p.supervisorId === sup);
  const activas = dia.asignaciones.filter((a) => a.estado === `ACTIVA`);
  const mias = activas.filter((a) => a.supervisorId === sup);
  const conQuien = Object.fromEntries(activas.map((a) => [a.trabajadorId, a]));
  const pasado = fecha < hoyISO();
  const editable = (puede || coordinador) && !pasado;

  // Cuánto de lo programado ya tiene a alguien.
  const asignadas = {};
  for (const a of mias) for (const p of a.puntos) asignadas[p.puntoId] = (asignadas[p.puntoId] ?? 0) + p.pasadas;
  const programadas = prog ? sumaPasadas(prog.puntos) : 0;
  const cubiertas = prog ? prog.puntos.reduce((s, p) => s + Math.min(p.pasadas, asignadas[p.puntoId] ?? 0), 0) : 0;
  const fueraDePlan = mias.flatMap((a) => a.puntos.filter((p) => !prog?.puntos.some((x) => x.puntoId === p.puntoId)).map((p) => ({ ...p, trabajador: a.trabajador })));

  const lista = trabajadores
    .filter((t) => t.activo && (verTodos || !conQuien[t.id]))
    .filter((t) => !buscar || `${t.nombre} ${t.dni ?? ``}`.toLowerCase().includes(buscar.toLowerCase()));

  const copiar = async () => {
    setAviso(``);
    try {
      const r = await api(`/labores/dia/${fecha}/copiar-anterior`, { method: `POST` });
      setAviso(r.copiadas ? `Se copiaron ${r.copiadas} asignaciones del día anterior.` : `No había nada nuevo para copiar del día anterior.`);
      onCambio();
    } catch (e) {
      setAviso(e.message);
    }
  };

  return (
    <div className="lb-asignar">
      <div className="lb-barra-acciones">
        <p className="lb-sub">Personal del {diaLargo(fecha)}. La lista le llega a cada trabajador por WhatsApp a las 6:00 de ese día.</p>
        <div className="lb-botones">
          {coordinador && (
            <select className={claseEntrada} value={sup ?? ``} onChange={(e) => setElegido(Number(e.target.value))} aria-label="Supervisor">
              {!supervisores.some((s) => s.id === yo) && <option value={yo}>Yo</option>}
              {supervisores.map((s) => <option key={s.id} value={s.id}>{s.id === yo ? `${s.nombre} (yo)` : s.nombre}</option>)}
            </select>
          )}
          {puede && !pasado && sup === yo && <button className={claseSecundario} onClick={copiar}>Copiar el día anterior</button>}
        </div>
      </div>
      {aviso && <p className="lb-sub">{aviso}</p>}

      <section className="lb-bloque">
        <h3>Programación del día{sup !== yo && supNombre ? ` · ${supNombre}` : ``}</h3>
        {!prog ? (
          <p className="lb-vacio">{sup === yo ? `Este día no está en tu plan de trabajo.` : `Este día no está en su plan de trabajo.`} Igual se puede asignar personal y puntos.</p>
        ) : (
          <>
            {prog.estado !== `VALIDADO` && (
              <p className="lb-nota">El plan de trabajo de esta semana está <b>{ESTADO_PLAN[prog.estado]?.texto.toLowerCase()}</b>: el coordinador todavía no lo valida.</p>
            )}
            <div className="lb-resumen">
              <Cifra valor={prog.puntos.length} etiqueta="puntos programados" />
              <Cifra valor={`${cubiertas} / ${programadas}`} etiqueta="pasadas con alguien asignado" barra={programadas ? Math.round((100 * cubiertas) / programadas) : 0} />
              <Cifra valor={mias.length} etiqueta="trabajadores elegidos" />
              <Cifra valor={programadas - cubiertas} etiqueta="pasadas sin asignar" alerta={programadas - cubiertas > 0} />
            </div>
            <div className="lb-tabla-caja">
              <table className="lb-tabla">
                <thead><tr><th>Punto</th><th>Fundo</th><th>Programadas</th><th>Asignadas</th><th>Quién</th></tr></thead>
                <tbody>
                  {prog.puntos.map((p) => {
                    const n = asignadas[p.puntoId] ?? 0;
                    const quien = mias.filter((a) => a.puntos.some((x) => x.puntoId === p.puntoId));
                    return (
                      <tr key={p.puntoId} className={n < p.pasadas ? `lb-fila-revisar` : ``}>
                        <td><span className="lb-codigo">{p.codigo}</span> {p.nombre}</td>
                        <td>{p.fundo}</td>
                        <td>{p.pasadas}</td>
                        <td className={n < p.pasadas ? `lb-aviso` : `lb-bien`}>{n}</td>
                        <td>{quien.length ? quien.map((a) => a.trabajador).join(`, `) : <span className="lb-sub">Nadie todavía</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        {fueraDePlan.length > 0 && (
          <p className="lb-sub">Fuera de la programación: {fueraDePlan.map((p) => `${p.codigo}×${p.pasadas} (${p.trabajador})`).join(` · `)}</p>
        )}
      </section>

      <div className="lb-asignar-cuerpo">
        <section className="lb-bloque">
          <h3>Personal elegido ({mias.length})</h3>
          {mias.length === 0 && <p className="lb-vacio">Todavía no eligió a nadie para este día.</p>}
          <div className="lb-lista">
            {mias.map((a) => {
              const t = trabajadores.find((x) => x.id === a.trabajadorId) ?? { id: a.trabajadorId, nombre: a.trabajador };
              return (
                <div key={a.id} className="lb-fila">
                  <div className="lb-fila-texto">
                    <strong>{a.trabajador}</strong>
                    <span className="lb-sub">{a.telefono?.replace(/^51/, ``)} · <span className={`lb-origen lb-origen-${(a.origen ?? `MANUAL`).toLowerCase()}`}>{ORIGEN[a.origen] ?? ORIGEN.MANUAL}</span></span>
                  </div>
                  <div className="lb-fila-puntos">{a.puntos.map((p) => <span key={p.id} className="lb-chip">{p.codigo} × {p.pasadas}</span>)}</div>
                  {editable && <button className={claseSecundario} onClick={() => setEditando({ t, a })}>Cambiar</button>}
                </div>
              );
            })}
          </div>
        </section>

        <section className="lb-bloque">
          <h3>Trabajadores activos</h3>
          <div className="lb-buscador">
            <input className={claseEntrada} value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar por nombre o DNI" />
            <label className="lb-check"><input type="checkbox" checked={verTodos} onChange={(e) => setVerTodos(e.target.checked)} /> Ver también a los ya elegidos</label>
          </div>
          <div className="lb-lista lb-lista-corta">
            {lista.map((t) => {
              const a = conQuien[t.id];
              return (
                <div key={t.id} className="lb-fila lb-fila-compacta">
                  <div className="lb-fila-texto">
                    <strong>{t.nombre}</strong>
                    <span className="lb-sub">DNI {t.dni ?? `—`}{a ? ` · con ${a.supervisorId === sup ? `este supervisor` : a.supervisor}` : ` · libre`}</span>
                  </div>
                  {editable && !a && <button className={clasePrimario} onClick={() => setEditando({ t })}>Elegir</button>}
                </div>
              );
            })}
            {lista.length === 0 && <p className="lb-sub">{trabajadores.some((t) => t.activo) ? `No hay trabajadores libres con ese filtro.` : `Primero agrega trabajadores en la pestaña «Trabajadores».`}</p>}
          </div>
        </section>
      </div>
      {editando && (
        <FormularioAsignacion fecha={fecha} {...editando} puntos={puntos} programacion={prog?.puntos ?? []} asignadas={asignadas} supervisorId={sup !== yo ? sup : null}
          onClose={() => setEditando(null)} onGuardado={() => { setEditando(null); onCambio(); }} />
      )}
      {maestros && <CierreDelDia fecha={fecha} />}
    </div>
  );
}

/** Hora de cierre del día y cierre manual (resumen por WhatsApp a cada supervisor). */
function CierreDelDia({ fecha }) {
  const [hora, setHora] = useState(``);
  const [mensaje, setMensaje] = useState(``);
  useEffect(() => { api(`/labores/configuracion`).then((c) => setHora(c.horaCierre)).catch(() => {}); }, []);
  const guardar = async () => {
    setMensaje(``);
    try {
      const c = await api(`/labores/configuracion`, { method: `PUT`, body: JSON.stringify({ horaCierre: hora }) });
      setHora(c.horaCierre);
      setMensaje(`Hora de cierre guardada: ${c.horaCierre}.`);
    } catch (e) {
      setMensaje(e.message);
    }
  };
  const cerrar = async () => {
    setMensaje(``);
    try {
      const r = await api(`/labores/cierre?fecha=${fecha}`, { method: `POST` });
      setMensaje(r.avisos ? `Se enviaron ${r.avisos} resúmenes a los supervisores.` : `Cierre anotado. No había supervisores con celular para avisar.`);
    } catch (e) {
      setMensaje(e.message);
    }
  };
  return (
    <section className="lb-bloque lb-cierre">
      <h3>Cierre del día</h3>
      <p className="lb-sub">A esta hora cada supervisor recibe por WhatsApp el avance de su gente y lo que quedó sin completar.</p>
      <div className="lb-cierre-fila">
        <input type="time" className={claseEntrada} value={hora} onChange={(e) => setHora(e.target.value)} aria-label="Hora de cierre" />
        <button className={claseSecundario} onClick={guardar}>Guardar hora</button>
        <button className={claseSecundario} onClick={cerrar}>Cerrar el día ahora</button>
      </div>
      {mensaje && <p className="lb-sub">{mensaje}</p>}
    </section>
  );
}

/**
 * Elegir puntos y pasadas. modo «asignar»: lo que hace un trabajador ese día (primero
 * lo programado, con lo que falta cubrir). modo «plan»: lo que el supervisor
 * programa en su plan de trabajo para uno o varios días de la semana.
 */
function FormularioAsignacion({ fecha, t, a, puntos, onClose, onGuardado, modo = `asignar`, semana = [], inicial: dado, programacion = [], asignadas = {}, supervisorId, lunes }) {
  const inicial = Object.fromEntries((dado ?? a?.puntos ?? []).map((p) => [p.puntoId, p.pasadas]));
  const [elegidos, setElegidos] = useState(inicial);
  const [dias, setDias] = useState([fecha]);
  const esPlan = modo === `plan`;
  const [error, setError] = useState(``);
  const [guardando, setGuardando] = useState(false);
  const activos = puntos.filter((p) => p.activo);
  const programados = new Set(programacion.map((p) => p.puntoId));
  const fundos = [...new Set(activos.filter((p) => !programados.has(p.id)).map((p) => p.fundo))].sort();
  const alternar = (id, sugerido = 1) => setElegidos((e) => { const n = { ...e }; if (n[id]) delete n[id]; else n[id] = sugerido; return n; });
  const guardar = async () => {
    setGuardando(true);
    setError(``);
    try {
      const lista = Object.entries(elegidos).map(([puntoId, pasadas]) => ({ puntoId: Number(puntoId), pasadas }));
      if (esPlan) {
        await api(`/labores/plan-trabajo/${lunes}`, { method: `PUT`, body: JSON.stringify({ fechas: dias, puntos: lista }) });
      } else {
        await api(`/labores/dia/${fecha}/trabajadores/${t.id}`, { method: `PUT`, body: JSON.stringify({ puntos: lista, supervisorId }) });
      }
      onGuardado();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };
  const opcion = (p, extra, sugerido) => (
    <label key={p.id} className={elegidos[p.id] ? `lb-opcion lb-opcion-on` : `lb-opcion`}>
      <input type="checkbox" checked={!!elegidos[p.id]} onChange={() => alternar(p.id, sugerido)} />
      <span className="lb-codigo">{p.codigo}</span>
      <span className="lb-opcion-nombre">{p.nombre}{extra && <span className="lb-sub"> · {extra}</span>}</span>
      {elegidos[p.id] && (
        <span className="lb-contador" onClick={(e) => e.preventDefault()}>
          <button type="button" onClick={() => setElegidos((x) => ({ ...x, [p.id]: Math.max(1, x[p.id] - 1) }))} aria-label="Menos pasadas">−</button>
          <b>{elegidos[p.id]}</b>
          <button type="button" onClick={() => setElegidos((x) => ({ ...x, [p.id]: Math.min(10, x[p.id] + 1) }))} aria-label="Más pasadas">+</button>
          <span className="lb-sub">pasadas</span>
        </span>
      )}
    </label>
  );
  return (
    <Modal titulo={esPlan ? `Programar · ${diaLargo(fecha)}` : `${t.nombre} · ${diaLargo(fecha)}`} onClose={onClose}>
      <div className="lb-form">
        <p className="lb-sub">{esPlan ? `Los puntos que tu equipo debe limpiar ese día y cuántas pasadas. El día anterior eliges a quién le toca cada uno.` : `Marca su parte de la programación y cuántas pasadas. Si la programación cambió, elige otros puntos.`}</p>
        {esPlan && (
          <div className="lb-grupo">
            <span className="lb-grupo-titulo">Aplicar a estos días</span>
            <div className="lb-filtros">
              {semana.filter((d) => d >= hoyISO()).map((d) => (
                <button key={d} type="button" className={dias.includes(d) ? `lb-filtro lb-filtro-on` : `lb-filtro`}
                  onClick={() => setDias((x) => (x.includes(d) ? (x.length > 1 ? x.filter((y) => y !== d) : x) : [...x, d]))}>{diaCorto(d)}</button>
              ))}
            </div>
          </div>
        )}
        {programacion.length > 0 && (
          <div className="lb-grupo">
            <span className="lb-grupo-titulo">De la programación del día</span>
            {programacion.map((x) => {
              const p = activos.find((y) => y.id === x.puntoId) ?? { id: x.puntoId, codigo: x.codigo, nombre: x.nombre };
              const propias = a?.puntos.find((y) => y.puntoId === x.puntoId)?.pasadas ?? 0;
              const falta = Math.max(0, x.pasadas - ((asignadas[x.puntoId] ?? 0) - propias));
              return opcion(p, falta ? `faltan ${falta} de ${x.pasadas}` : `cubierto`, Math.max(1, falta));
            })}
          </div>
        )}
        {fundos.map((f) => (
          <div key={f} className="lb-grupo">
            <span className="lb-grupo-titulo">{programacion.length ? `Otros puntos · ${f}` : f}</span>
            {activos.filter((p) => p.fundo === f && !programados.has(p.id)).map((p) => opcion(p))}
          </div>
        ))}
        {error && <p className="lb-error">{error}</p>}
        <div className="lb-botones">
          {Object.keys(inicial).length > 0 && <button className={claseSecundario} onClick={() => setElegidos({})}>{esPlan ? `Dejar el día libre` : `Quitarle todo`}</button>}
          <button className={claseSecundario} onClick={onClose}>Cancelar</button>
          <button className={clasePrimario} disabled={guardando} onClick={guardar}>{guardando ? `Guardando…` : `Guardar`}</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Trabajadores (lista libre: sin fundo ni supervisor fijo) ──

const edad = (iso) => {
  if (!iso) return null;
  const n = new Date(`${iso}T12:00:00`), h = new Date();
  return h.getFullYear() - n.getFullYear() - (h < new Date(h.getFullYear(), n.getMonth(), n.getDate()) ? 1 : 0);
};

function Trabajadores({ lista, onCambio, puede }) {
  const [editar, setEditar] = useState(null);
  const [importar, setImportar] = useState(false);
  const [buscar, setBuscar] = useState(``);
  const [estado, setEstado] = useState(`activos`);
  const vis = lista
    .filter((t) => (estado === `todos` ? true : estado === `activos` ? t.activo : !t.activo))
    .filter((t) => !buscar || `${t.nombre} ${t.dni ?? ``} ${t.telefono}`.toLowerCase().includes(buscar.toLowerCase()));
  const incompletos = lista.filter((t) => t.activo && (!t.dni || !t.fechaNacimiento || !t.apellidoPaterno)).length;
  return (
    <div className="lb-maestro">
      <div className="lb-barra-acciones">
        <p className="lb-sub">Lista general de trabajadores de limpieza. No son usuarios del sistema: el bot los reconoce por su celular. Cada día los supervisores eligen de aquí a los activos.</p>
        {puede && (
          <div className="lb-botones">
            <button className={claseSecundario} onClick={() => setImportar(true)}>Importar desde Excel</button>
            <button className={clasePrimario} onClick={() => setEditar({})}>Nuevo trabajador</button>
          </div>
        )}
      </div>
      <div className="lb-barra-acciones">
        <div className="lb-filtros">
          {[[`activos`, `Activos`], [`inactivos`, `Inactivos`], [`todos`, `Todos`]].map(([k, v]) => (
            <button key={k} className={estado === k ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => setEstado(k)}>
              {v} ({k === `todos` ? lista.length : lista.filter((t) => t.activo === (k === `activos`)).length})
            </button>
          ))}
        </div>
        <input className={`${claseEntrada} lb-buscar`} value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar por nombre, DNI o celular" />
      </div>
      {incompletos > 0 && <p className="lb-nota">{incompletos} {incompletos === 1 ? `trabajador activo tiene` : `trabajadores activos tienen`} datos incompletos (apellidos, DNI o fecha de nacimiento). Complétalos con «Editar» o con «Importar desde Excel».</p>}
      <div className="lb-tabla-caja">
        <table className="lb-tabla">
          <thead><tr><th>Apellidos y nombres</th><th>DNI</th><th>Nacimiento</th><th>Celular</th><th>Estado</th>{puede && <th />}</tr></thead>
          <tbody>
            {vis.map((t) => (
              <tr key={t.id} className={t.activo ? `` : `lb-inactivo`}>
                <td>{t.apellidoPaterno ? <><strong>{[t.apellidoPaterno, t.apellidoMaterno].filter(Boolean).join(` `)}</strong>, {t.nombres}</> : t.nombre}</td>
                <td>{t.dni ?? <span className="lb-aviso">falta</span>}</td>
                <td>{t.fechaNacimiento ? `${new Date(`${t.fechaNacimiento}T12:00:00`).toLocaleDateString(`es-PE`)} · ${edad(t.fechaNacimiento)} años` : <span className="lb-aviso">falta</span>}</td>
                <td>{t.telefono.replace(/^51/, ``)}</td>
                <td>{t.activo ? `Activo` : `Inactivo`}</td>
                {puede && <td><button className="lb-filtro" onClick={() => setEditar(t)}>Editar</button></td>}
              </tr>
            ))}
            {vis.length === 0 && <tr><td colSpan={6} className="lb-sub">{lista.length ? `Nadie con ese filtro.` : `Todavía no hay trabajadores.`}</td></tr>}
          </tbody>
        </table>
      </div>
      {editar && <FormularioTrabajador t={editar} onClose={() => setEditar(null)} onGuardado={() => { setEditar(null); onCambio(); }} />}
      {importar && <ImportarTrabajadores onClose={() => setImportar(false)} onListo={() => { setImportar(false); onCambio(); }} />}
    </div>
  );
}

// ── Importar trabajadores desde Excel (2026-10-31, piloto de ~80 personas) ──

const COLUMNAS = [
  { campo: `nombres`, titulo: `Nombres`, claves: [`nombres`, `nombre`] },
  { campo: `apellidoPaterno`, titulo: `Apellido paterno`, claves: [`apellidopaterno`, `apepaterno`, `paterno`, `primerapellido`] },
  { campo: `apellidoMaterno`, titulo: `Apellido materno`, claves: [`apellidomaterno`, `apematerno`, `materno`, `segundoapellido`] },
  { campo: `apellidos`, titulo: `Apellidos`, claves: [`apellidos`] },
  { campo: `dni`, titulo: `DNI`, claves: [`dni`, `documento`, `nrodocumento`, `numerodocumento`, `nrodni`] },
  { campo: `fechaNacimiento`, titulo: `Fecha de nacimiento`, claves: [`fechadenacimiento`, `fechanacimiento`, `nacimiento`, `fnacimiento`, `fechanac`] },
  { campo: `telefono`, titulo: `Celular`, claves: [`celular`, `telefono`, `cel`, `movil`, `whatsapp`, `nrocelular`] },
];
const claveDe = (s) => String(s).normalize(`NFD`).replace(/[̀-ͯ]/g, ``).toLowerCase().replace(/[^a-z]/g, ``);
const dosDig = (n) => String(n).padStart(2, `0`);

/** Fecha de Excel (número de serie), Date o texto dd/mm/aaaa o aaaa-mm-dd → aaaa-mm-dd; si no se entiende o no existe (30/02), null. */
function fechaISO(v) {
  const iso = fechaTexto(v);
  if (!iso) return null;
  const [a, m, d] = iso.split(`-`).map(Number);
  const f = new Date(Date.UTC(a, m - 1, d));
  return f.getUTCFullYear() === a && f.getUTCMonth() === m - 1 && f.getUTCDate() === d ? iso : null;
}

function fechaTexto(v) {
  if (v === `` || v == null) return null;
  if (typeof v === `number`) {
    const d = new Date(Date.UTC(1899, 11, 30) + Math.round(v) * 86400000);
    return `${d.getUTCFullYear()}-${dosDig(d.getUTCMonth() + 1)}-${dosDig(d.getUTCDate())}`;
  }
  if (v instanceof Date) return `${v.getFullYear()}-${dosDig(v.getMonth() + 1)}-${dosDig(v.getDate())}`;
  const t = String(v).trim();
  let m = t.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m) return `${m[1]}-${dosDig(m[2])}-${dosDig(m[3])}`;
  m = t.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (m) {
    const anio = m[3].length === 2 ? (Number(m[3]) > 30 ? `19${m[3]}` : `20${m[3]}`) : m[3];
    return `${anio}-${dosDig(m[2])}-${dosDig(m[1])}`;
  }
  return null;
}

/** Lee la primera hoja: reconoce las columnas por su título y arma un trabajador por fila. */
async function leerExcel(archivo) {
  const libro = XLSX.read(await archivo.arrayBuffer(), { type: `array` });
  const hoja = libro.Sheets[libro.SheetNames[0]];
  // Con las filas vacías incluidas, la posición da el número de fila del Excel (para los mensajes).
  const filas = XLSX.utils.sheet_to_json(hoja, { header: 1, defval: ``, raw: true, blankrows: true });
  const primera = XLSX.utils.decode_range(hoja[`!ref`] ?? `A1`).s.r + 1;
  const iCab = filas.findIndex((f) => f.some((c) => COLUMNAS.some((x) => x.claves.includes(claveDe(c)))));
  if (iCab < 0) throw new Error(`No encontré los títulos de las columnas (Nombres, Apellido paterno, DNI, Fecha de nacimiento, Celular). Usa la plantilla.`);
  const indice = {};
  filas[iCab].forEach((c, i) => {
    const col = COLUMNAS.find((x) => x.claves.includes(claveDe(c)));
    if (col && indice[col.campo] == null) indice[col.campo] = i;
  });
  const faltan = [`nombres`, `dni`, `telefono`].filter((c) => indice[c] == null).map((c) => COLUMNAS.find((x) => x.campo === c).titulo);
  if (indice.apellidoPaterno == null && indice.apellidos == null) faltan.push(`Apellido paterno`);
  if (faltan.length) throw new Error(`Faltan columnas: ${faltan.join(`, `)}.`);
  const val = (f, campo) => (indice[campo] == null ? `` : f[indice[campo]]);
  const texto = (v) => String(v ?? ``).trim();
  return filas.slice(iCab + 1)
    .map((f, i) => ({ f, fila: primera + iCab + 1 + i }))
    .filter(({ f }) => f.some((c) => texto(c) !== ``))
    .map(({ f, fila }) => {
      let paterno = texto(val(f, `apellidoPaterno`)), materno = texto(val(f, `apellidoMaterno`));
      if (!paterno && indice.apellidos != null) [paterno, materno = ``] = texto(val(f, `apellidos`)).split(/\s+(.*)/);
      const dni = texto(val(f, `dni`)).replace(/\D/g, ``);
      return {
        nombres: texto(val(f, `nombres`)),
        apellidoPaterno: paterno,
        apellidoMaterno: materno,
        dni: dni && dni.length < 8 ? dni.padStart(8, `0`) : dni,
        fechaNacimiento: fechaISO(val(f, `fechaNacimiento`)),
        telefono: texto(val(f, `telefono`)).replace(/\D/g, ``),
        fila,
      };
    });
}

function descargarPlantilla() {
  // Sin fila de ejemplo, para que nadie la importe por error: las indicaciones van en otra hoja.
  const hoja = XLSX.utils.aoa_to_sheet([[`Nombres`, `Apellido paterno`, `Apellido materno`, `DNI`, `Fecha de nacimiento`, `Celular`]]);
  hoja[`!cols`] = [{ wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 11 }, { wch: 20 }, { wch: 12 }];
  const ayuda = XLSX.utils.aoa_to_sheet([
    [`Cómo llenar la hoja «Trabajadores»`],
    [`Una fila por trabajador, desde la fila 2.`],
    [`Nombres y apellido paterno son obligatorios; el materno puede quedar vacío.`],
    [`DNI: 8 dígitos. Si Excel le quita el cero inicial, se completa solo.`],
    [`Fecha de nacimiento: dd/mm/aaaa (por ejemplo 15/03/1978). Debe tener entre 18 y 80 años.`],
    [`Celular: 9 dígitos que empiezan con 9; es el número con el que escribe al bot por WhatsApp.`],
    [`Si el DNI ya está en el sistema, se actualizan sus datos. Si un trabajador antiguo no tenía DNI, se le reconoce por su celular.`],
  ]);
  ayuda[`!cols`] = [{ wch: 110 }];
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, `Trabajadores`);
  XLSX.utils.book_append_sheet(libro, ayuda, `Cómo llenarla`);
  XLSX.writeFile(libro, `trabajadores-labores.xlsx`);
}

const RESULTADO = {
  NUEVO: { texto: `Nuevo`, clase: `lb-bien` },
  ACTUALIZA: { texto: `Se actualiza`, clase: `lb-aviso` },
  IGUAL: { texto: `Sin cambios`, clase: `lb-sub` },
  ERROR: { texto: `No se importa`, clase: `lb-mal` },
};

const pedido = (lista, aplicar) => ({
  trabajadores: lista.map(({ fila, ...t }) => t),
  filas: lista.map((t) => t.fila ?? null),
  aplicar,
});

function ImportarTrabajadores({ onClose, onListo }) {
  const [filas, setFilas] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [error, setError] = useState(``);
  const [cargando, setCargando] = useState(false);

  const leer = async (archivo) => {
    if (!archivo) return;
    setError(``);
    setCargando(true);
    try {
      const lista = await leerExcel(archivo);
      if (!lista.length) throw new Error(`El archivo no tiene trabajadores debajo de los títulos.`);
      const r = await api(`/labores/trabajadores/importar`, { method: `POST`, body: JSON.stringify(pedido(lista, false)) });
      setFilas(lista);
      setPrevia(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  };
  const importar = async () => {
    setError(``);
    try {
      await api(`/labores/trabajadores/importar`, { method: `POST`, body: JSON.stringify(pedido(filas, true)) });
      onListo();
    } catch (e) {
      setError(e.message);
    }
  };

  const cuenta = (a) => previa?.filter((f) => f.accion === a).length ?? 0;
  const aGuardar = cuenta(`NUEVO`) + cuenta(`ACTUALIZA`);
  // Primero lo que no entra y lo que cambia a alguien que ya existe, para revisarlo.
  const orden = { ERROR: 0, ACTUALIZA: 1, NUEVO: 2, IGUAL: 3 };
  return (
    <Modal titulo="Importar trabajadores desde Excel" onClose={onClose} ancho="max-w-3xl">
      <div className="lb-form">
        {!previa ? (
          <>
            <p className="lb-sub">
              Una fila por trabajador con nombres, apellido paterno, apellido materno, DNI, fecha de nacimiento y celular.
              Si el DNI ya está, se actualizan sus datos; si un trabajador antiguo no tenía DNI, se le reconoce por su celular y se completa.
              Los que vengan en la lista quedan activos.
            </p>
            <div className="lb-botones lb-botones-izq">
              <button type="button" className={claseSecundario} onClick={descargarPlantilla}>Descargar la plantilla</button>
            </div>
            <label>Archivo<input type="file" accept=".xlsx,.xls,.csv" className={claseEntrada} onChange={(e) => leer(e.target.files?.[0])} disabled={cargando} /></label>
            {cargando && <p className="lb-sub">Revisando…</p>}
          </>
        ) : (
          <>
            <div className="lb-resumen">
              <Cifra valor={cuenta(`NUEVO`)} etiqueta="nuevos" />
              <Cifra valor={cuenta(`ACTUALIZA`)} etiqueta="se actualizan" />
              <Cifra valor={cuenta(`IGUAL`)} etiqueta="sin cambios" />
              <Cifra valor={cuenta(`ERROR`)} etiqueta="con errores (no se importan)" alerta={cuenta(`ERROR`) > 0} />
            </div>
            <div className="lb-tabla-caja lb-lista-corta">
              <table className="lb-tabla">
                <thead><tr><th>Fila</th><th>Trabajador</th><th>DNI</th><th>Celular</th><th>Resultado</th></tr></thead>
                <tbody>
                  {[...previa].sort((x, y) => orden[x.accion] - orden[y.accion] || x.fila - y.fila).map((f) => (
                    <tr key={f.fila}>
                      <td>{f.fila}</td>
                      <td>{f.nombre || `—`}</td>
                      <td>{f.dni || `—`}</td>
                      <td>{f.telefono?.replace(/^51/, ``) || `—`}</td>
                      <td><span className={RESULTADO[f.accion].clase}>{RESULTADO[f.accion].texto}</span>{f.error && <span className="lb-sub"><br />{f.error}</span>}{f.nota && <span className="lb-sub"><br />{f.nota}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {cuenta(`ERROR`) > 0 && <p className="lb-sub">Las filas con error no se importan. Corrígelas en el Excel y vuelve a subirlo; las que ya entraron saldrán «sin cambios».</p>}
            <div className="lb-botones">
              <button className={claseSecundario} onClick={() => { setPrevia(null); setFilas(null); }} disabled={cargando}>Otro archivo</button>
              <AccionAsync onClick={importar} disabled={aGuardar === 0} textoOcupado="Importando…">
                {aGuardar === 0 ? `Nada que importar` : `Importar ${aGuardar} ${aGuardar === 1 ? `trabajador` : `trabajadores`}`}
              </AccionAsync>
            </div>
          </>
        )}
        {error && <p className="lb-error">{error}</p>}
      </div>
    </Modal>
  );
}

function FormularioTrabajador({ t, onClose, onGuardado }) {
  const [d, setD] = useState({
    nombres: t.nombres ?? ``, apellidoPaterno: t.apellidoPaterno ?? ``, apellidoMaterno: t.apellidoMaterno ?? ``,
    dni: t.dni ?? ``, fechaNacimiento: t.fechaNacimiento ?? ``, telefono: (t.telefono ?? ``).replace(/^51/, ``), activo: t.id ? t.activo : true,
  });
  const [error, setError] = useState(``);
  const guardar = async (e) => {
    e.preventDefault();
    setError(``);
    try {
      await api(`/labores/trabajadores`, { method: `POST`, body: JSON.stringify({ id: t.id ?? null, ...d, fechaNacimiento: d.fechaNacimiento || null }) });
      onGuardado();
    } catch (x) {
      setError(x.message);
    }
  };
  const campo = (k) => ({ value: d[k], onChange: (e) => setD({ ...d, [k]: e.target.value }) });
  return (
    <Modal titulo={t.id ? t.nombre : `Nuevo trabajador`} onClose={onClose}>
      <form className="lb-form" onSubmit={guardar}>
        <label>Nombres<input className={claseEntrada} {...campo(`nombres`)} required maxLength={80} /></label>
        <div className="lb-dos">
          <label>Apellido paterno<input className={claseEntrada} {...campo(`apellidoPaterno`)} required maxLength={60} /></label>
          <label>Apellido materno<input className={claseEntrada} {...campo(`apellidoMaterno`)} maxLength={60} /></label>
        </div>
        <div className="lb-dos">
          <label>DNI<input className={claseEntrada} inputMode="numeric" maxLength={8} value={d.dni} onChange={(e) => setD({ ...d, dni: e.target.value.replace(/\D/g, ``) })} required placeholder="12345678" /></label>
          <label>Fecha de nacimiento<input type="date" className={claseEntrada} {...campo(`fechaNacimiento`)} required max={hoyISO()} /></label>
        </div>
        <label>Celular (WhatsApp)<input className={claseEntrada} inputMode="numeric" maxLength={9} value={d.telefono} onChange={(e) => setD({ ...d, telefono: e.target.value.replace(/\D/g, ``) })} required placeholder="987654321" /></label>
        <span className="lb-sub">Con este celular manda al bot las fotos y la ubicación de cada pasada.</span>
        {t.id && <label className="lb-check"><input type="checkbox" checked={d.activo} onChange={(e) => setD({ ...d, activo: e.target.checked })} /> Activo (si dejó de venir, desmárcalo y ya no aparece para asignar)</label>}
        {error && <p className="lb-error">{error}</p>}
        <div className="lb-botones">
          <button type="button" className={claseSecundario} onClick={onClose}>Cancelar</button>
          <button type="submit" className={clasePrimario}>Guardar</button>
        </div>
      </form>
    </Modal>
  );
}

// ── Puntos ──

function Puntos({ lista, onCambio, puede }) {
  const [editar, setEditar] = useState(null);
  const [importar, setImportar] = useState(false);
  const [vista, setVista] = useState(`lista`);
  const fundos = vista === `lista` ? [...new Set(lista.map((p) => p.fundo))] : [];
  return (
    <div className="lb-maestro">
      <div className="lb-barra-acciones">
        <p className="lb-sub">SS.HH. y comedores con su ubicación. Se cargan desde el KMZ de activos; los que tengan el mismo código se actualizan.</p>
        {puede && (
          <div className="lb-botones">
            <button className={claseSecundario} onClick={() => setImportar(true)}>Importar KMZ</button>
            <button className={clasePrimario} onClick={() => setEditar({})}>Nuevo punto</button>
          </div>
        )}
      </div>
      {importar && <ImportarKmz existentes={lista} onClose={() => setImportar(false)} onListo={() => { setImportar(false); onCambio(); }} />}
      <div className="lb-filtros">
        <button className={vista === `lista` ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => { setVista(`lista`); onCambio(); }}>Lista</button>
        <button className={vista === `mapa` ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => setVista(`mapa`)}>Mapa</button>
        {vista === `mapa` && puede && <span className="lb-sub">Arrastra un punto para corregir su ubicación; se guarda al soltarlo.</span>}
      </div>
      {vista === `mapa` && <MapaPuntos puntos={lista} editable={puede} onCambio={onCambio} />}
      {fundos.map((f) => (
        <section key={f} className="lb-bloque">
          <h3>{f}</h3>
          <div className="lb-lista">
            {lista.filter((p) => p.fundo === f).map((p) => (
              <div key={p.id} className={p.activo ? `lb-fila` : `lb-fila lb-inactivo`}>
                <span className="lb-codigo">{p.codigo}</span>
                <div className="lb-fila-texto">
                  <strong>{p.nombre}</strong>
                  <span className="lb-sub">{p.tipo === `COMEDOR` ? `Comedor` : `SS.HH.`} · {p.lat != null ? `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)} · radio ${p.radioM} m` : `sin ubicación`}</span>
                </div>
                {puede && <button className={claseSecundario} onClick={() => setEditar(p)}>Editar</button>}
              </div>
            ))}
          </div>
        </section>
      ))}
      {editar && <FormularioPunto p={editar} onClose={() => setEditar(null)} onGuardado={() => { setEditar(null); onCambio(); }} />}
    </div>
  );
}

function FormularioPunto({ p, onClose, onGuardado }) {
  const [d, setD] = useState({ codigo: p.codigo ?? ``, nombre: p.nombre ?? ``, tipo: p.tipo ?? `SSHH`, fundo: p.fundo ?? ``, lat: p.lat ?? ``, lng: p.lng ?? ``, radioM: p.radioM ?? 60, activo: p.id ? p.activo : true });
  const [fundos, setFundos] = useState([]);
  const [error, setError] = useState(``);
  useEffect(() => { api(`/fundos`).then((f) => setFundos(f.map((x) => x.nombre))).catch(() => {}); }, []);
  const guardar = async (e) => {
    e.preventDefault();
    setError(``);
    try {
      await api(`/labores/puntos`, {
        method: `POST`,
        body: JSON.stringify({ id: p.id ?? null, ...d, lat: d.lat === `` ? null : Number(d.lat), lng: d.lng === `` ? null : Number(d.lng), radioM: Number(d.radioM) || 60 }),
      });
      onGuardado();
    } catch (x) {
      setError(x.message);
    }
  };
  return (
    <Modal titulo={p.id ? p.codigo : `Nuevo punto`} onClose={onClose}>
      <form className="lb-form" onSubmit={guardar}>
        <div className="lb-dos">
          <label>Código<input className={claseEntrada} value={d.codigo} onChange={(e) => setD({ ...d, codigo: e.target.value.toUpperCase() })} required placeholder="CH-01" /></label>
          <label>Tipo
            <select className={claseEntrada} value={d.tipo} onChange={(e) => setD({ ...d, tipo: e.target.value })}>
              <option value="SSHH">SS.HH.</option>
              <option value="COMEDOR">Comedor</option>
            </select>
          </label>
        </div>
        <label>Nombre<input className={claseEntrada} value={d.nombre} onChange={(e) => setD({ ...d, nombre: e.target.value })} /></label>
        <label>Fundo
          <select className={claseEntrada} value={d.fundo} onChange={(e) => setD({ ...d, fundo: e.target.value })} required>
            <option value="" disabled>Elige el fundo</option>
            {[...new Set([...fundos, ...(d.fundo ? [d.fundo] : [])])].map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
        <div className="lb-dos">
          <label>Latitud<input className={claseEntrada} inputMode="decimal" value={d.lat} onChange={(e) => setD({ ...d, lat: e.target.value })} placeholder="-14.06420" /></label>
          <label>Longitud<input className={claseEntrada} inputMode="decimal" value={d.lng} onChange={(e) => setD({ ...d, lng: e.target.value })} placeholder="-75.72880" /></label>
        </div>
        <label>Radio de tolerancia (m)<input className={claseEntrada} inputMode="numeric" value={d.radioM} onChange={(e) => setD({ ...d, radioM: e.target.value.replace(/\D/g, ``) })} /></label>
        {p.id && <label className="lb-check"><input type="checkbox" checked={d.activo} onChange={(e) => setD({ ...d, activo: e.target.checked })} /> Activo</label>}
        {error && <p className="lb-error">{error}</p>}
        <div className="lb-botones">
          <button type="button" className={claseSecundario} onClick={onClose}>Cancelar</button>
          <button type="submit" className={clasePrimario}>Guardar</button>
        </div>
      </form>
    </Modal>
  );
}


/**
 * Importar puntos desde el KMZ de activos: se sube el archivo, se ve cada carpeta
 * con sus marcas y se elige cuáles entran, con qué fundo y de qué tipo.
 */
function ImportarKmz({ existentes, onClose, onListo }) {
  const [carpetas, setCarpetas] = useState(null);
  const [eleccion, setEleccion] = useState({});
  const [fundos, setFundos] = useState([]);
  const [estado, setEstado] = useState(``);
  const [cargando, setCargando] = useState(false);
  useEffect(() => { api(`/fundos`).then((f) => setFundos(f.map((x) => x.nombre))).catch(() => {}); }, []);
  const codigos = new Set(existentes.map((p) => p.codigo));

  const adivinar = (ruta) => {
    const r = ruta.toUpperCase();
    return {
      incluir: /SS\.?\s?HH|SSHH|BAÑO|BANO|COMEDOR/.test(r),
      tipo: /COMEDOR/.test(r) ? `COMEDOR` : `SSHH`,
      fundo: fundos.find((f) => r.includes(f)) ?? ``,
    };
  };

  const leer = async (archivo) => {
    if (!archivo) return;
    setCargando(true);
    setEstado(``);
    try {
      const datos = new FormData();
      datos.append(`archivo`, archivo);
      const r = await fetch(`${API_BASE}/labores/puntos/kmz`, {
        method: `POST`,
        headers: { Authorization: `Bearer ${localStorage.getItem(`gth_auth_token`) ?? ``}` },
        body: datos,
      });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error(j?.mensaje ?? j?.message ?? `No se pudo leer el archivo`);
      setCarpetas(j);
      setEleccion(Object.fromEntries(j.map((c) => [c.ruta, adivinar(c.ruta)])));
    } catch (e) {
      setEstado(e.message);
    } finally {
      setCargando(false);
    }
  };

  const elegidas = (carpetas ?? []).filter((c) => eleccion[c.ruta]?.incluir);
  const total = elegidas.reduce((s, c) => s + c.marcas.length, 0);
  const faltaFundo = elegidas.some((c) => !eleccion[c.ruta]?.fundo);

  const importarAhora = async () => {
    setCargando(true);
    setEstado(``);
    try {
      const puntos = elegidas.flatMap((c) => c.marcas.map((m) => ({
        codigo: m.nombre, nombre: m.descripcion || m.nombre, tipo: eleccion[c.ruta].tipo, fundo: eleccion[c.ruta].fundo, lat: m.lat, lng: m.lng,
      })));
      const r = await api(`/labores/puntos/importar`, { method: `POST`, body: JSON.stringify({ puntos }) });
      setEstado(`Listo: ${r.nuevos} nuevos y ${r.actualizados} actualizados.`);
      setTimeout(onListo, 900);
    } catch (e) {
      setEstado(e.message);
    } finally {
      setCargando(false);
    }
  };

  const cambiar = (ruta, campo, valor) => setEleccion((x) => ({ ...x, [ruta]: { ...x[ruta], [campo]: valor } }));

  return (
    <Modal titulo="Importar puntos desde el KMZ" onClose={onClose}>
      <div className="lb-form">
        {!carpetas ? (
          <>
            <p className="lb-sub">Sube el .kmz (o .kml) exportado de Google Earth. Después eliges qué carpetas son SS.HH. o comedores y de qué fundo. El nombre de cada marca es su código (p. ej. CH-01).</p>
            <label>Archivo<input type="file" accept=".kmz,.kml" className={claseEntrada} onChange={(e) => leer(e.target.files?.[0])} disabled={cargando} /></label>
            {cargando && <p className="lb-sub">Leyendo…</p>}
          </>
        ) : (
          <>
            <p className="lb-sub">{carpetas.length} carpetas en el archivo. Marca las que entran como puntos de limpieza.</p>
            <div className="lb-kmz">
              {carpetas.map((c) => {
                const e = eleccion[c.ruta] ?? {};
                const nuevos = c.marcas.filter((m) => !codigos.has(m.nombre.toUpperCase())).length;
                return (
                  <div key={c.ruta} className={e.incluir ? `lb-kmz-carpeta lb-opcion-on` : `lb-kmz-carpeta`}>
                    <label className="lb-check">
                      <input type="checkbox" checked={!!e.incluir} onChange={(x) => cambiar(c.ruta, `incluir`, x.target.checked)} />
                      <strong>{c.ruta}</strong>
                      <span className="lb-sub">{c.marcas.length} marcas · {nuevos} nuevas</span>
                    </label>
                    {e.incluir && (
                      <div className="lb-dos">
                        <select className={claseEntrada} value={e.fundo} onChange={(x) => cambiar(c.ruta, `fundo`, x.target.value)} aria-label="Fundo">
                          <option value="">Elige el fundo</option>
                          {fundos.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                        <select className={claseEntrada} value={e.tipo} onChange={(x) => cambiar(c.ruta, `tipo`, x.target.value)} aria-label="Tipo">
                          <option value="SSHH">SS.HH.</option>
                          <option value="COMEDOR">Comedor</option>
                        </select>
                      </div>
                    )}
                    <p className="lb-sub lb-kmz-muestra">{c.marcas.slice(0, 6).map((m) => m.nombre).join(` · `)}{c.marcas.length > 6 ? ` …` : ``}</p>
                  </div>
                );
              })}
            </div>
            {faltaFundo && <p className="lb-error">Elige el fundo de cada carpeta marcada.</p>}
            <div className="lb-botones">
              <button className={claseSecundario} onClick={() => setCarpetas(null)}>Otro archivo</button>
              <button className={clasePrimario} disabled={cargando || total === 0 || faltaFundo} onClick={importarAhora}>{cargando ? `Importando…` : `Importar ${total} puntos`}</button>
            </div>
          </>
        )}
        {estado && <p className="lb-sub">{estado}</p>}
      </div>
    </Modal>
  );
}


// ── Plan de trabajo (cada supervisor arma su semana el sábado; el coordinador la valida) ──

function PlanDeTrabajo({ puntos, puede, coordinador }) {
  const yo = obtenerUsuario()?.id;
  // El sábado y el domingo se arma la semana que viene.
  const [lunes, setLunes] = useState(lunesDe(sumarDias(hoyISO(), 2)));
  const [planes, setPlanes] = useState(null);
  const [supervisores, setSupervisores] = useState([]);
  const [vista, setVista] = useState(puede && !coordinador ? `mio` : `consolidado`);
  const [editando, setEditando] = useState(null);
  const [revisando, setRevisando] = useState(null);
  const [aviso, setAviso] = useState(``);
  const semana = Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i));
  const cargar = useCallback(() => {
    api(`/labores/plan-trabajo?lunes=${lunes}`, { sinCache: true }).then(setPlanes).catch((e) => setAviso(e.message));
  }, [lunes]);
  useEffect(() => { setAviso(``); cargar(); }, [cargar]);
  useEffect(() => { api(`/labores/supervisores`).then(setSupervisores).catch(() => {}); }, []);
  const hoy = hoyISO();
  const mio = (planes ?? []).find((p) => p.supervisorId === yo);
  const editableMio = puede && semana[6] >= hoy && (!mio || mio.estado === `BORRADOR` || mio.estado === `OBSERVADO`);

  const accion = async (ruta, texto) => {
    setAviso(``);
    try {
      const r = await api(ruta, { method: `POST` });
      setAviso(texto(r));
      cargar();
    } catch (e) {
      setAviso(e.message);
    }
  };

  return (
    <div className="lb-asignar">
      <div className="lb-barra-acciones">
        <div className="lb-filtros">
          <button className="lb-filtro" onClick={() => setLunes(sumarDias(lunes, -7))} aria-label="Semana anterior">←</button>
          <span className="lb-semana">Semana del {diaLargo(semana[0])} al {diaLargo(semana[6])}</span>
          <button className="lb-filtro" onClick={() => setLunes(sumarDias(lunes, 7))} aria-label="Semana siguiente">→</button>
        </div>
        {puede && (
          <div className="lb-filtros">
            <button className={vista === `mio` ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => setVista(`mio`)}>Mi programación</button>
            <button className={vista === `consolidado` ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => setVista(`consolidado`)}>Plan de trabajo (todos)</button>
          </div>
        )}
      </div>
      {aviso && <p className="lb-sub">{aviso}</p>}
      {!planes ? <p className="lb-vacio">Cargando…</p> : vista === `mio` ? (
        <MiProgramacion plan={mio} semana={semana} hoy={hoy} editable={editableMio}
          onEditar={(d) => setEditando({ d, inicial: (mio?.puntos ?? []).filter((p) => p.fecha === d) })}
          onCopiar={() => accion(`/labores/plan-trabajo/${lunes}/copiar-anterior`, (r) => (r.copiados ? `Se copiaron ${r.copiados} puntos de tu semana anterior.` : `No había nada que copiar (o esos días ya estaban programados).`))}
          onEnviar={() => accion(`/labores/plan-trabajo/${lunes}/enviar`, () => `Plan enviado al coordinador para su validación.`)} />
      ) : (
        <Consolidado planes={planes} supervisores={supervisores} semana={semana} hoy={hoy} coordinador={coordinador} onRevisar={setRevisando}
          onValidar={(p) => api(`/labores/plan-trabajo/revisar/${p.id}`, { method: `POST`, body: JSON.stringify({ validar: true }) }).then(cargar).catch((e) => setAviso(e.message))} />
      )}
      {editando && (
        <FormularioAsignacion modo="plan" lunes={lunes} fecha={editando.d} semana={semana} inicial={editando.inicial} puntos={puntos}
          onClose={() => setEditando(null)} onGuardado={() => { setEditando(null); cargar(); }} />
      )}
      {revisando && <DevolverPlan plan={revisando} onClose={() => setRevisando(null)} onListo={() => { setRevisando(null); cargar(); }} />}
    </div>
  );
}

function MiProgramacion({ plan, semana, hoy, editable, onEditar, onCopiar, onEnviar }) {
  const estado = ESTADO_PLAN[plan?.estado ?? `BORRADOR`];
  const total = sumaPasadas(plan?.puntos ?? []);
  return (
    <>
      <div className="lb-barra-acciones">
        <p className="lb-sub">
          <span className={estado.clase}>{estado.texto}</span>{` `}
          {plan?.estado === `ENVIADO` && `Enviado ${new Date(plan.enviadoEn).toLocaleString(`es-PE`, { weekday: `short`, hour: `2-digit`, minute: `2-digit` })}. El coordinador lo está revisando.`}
          {plan?.estado === `VALIDADO` && `Validado por ${plan.revisadoPor ?? `el coordinador`}. Los cambios del día se hacen al asignar al personal.`}
          {(!plan || plan.estado === `BORRADOR`) && `Programa cada día los puntos y pasadas de tu equipo y envíalo al coordinador (el sábado).`}
        </p>
        {editable && (
          <div className="lb-botones">
            <button className={claseSecundario} onClick={onCopiar}>Copiar mi semana anterior</button>
            <button className={clasePrimario} onClick={onEnviar} disabled={total === 0}>Enviar al coordinador</button>
          </div>
        )}
      </div>
      {plan?.estado === `OBSERVADO` && <p className="lb-nota lb-nota-alerta"><b>Observación de {plan.revisadoPor ?? `el coordinador`}:</b> {plan.observacion}. Corrige y vuelve a enviarlo.</p>}
      <div className="lb-tabla-caja">
        <table className="lb-tabla lb-plan">
          <thead><tr><th>Día</th><th>Puntos y pasadas</th><th>Pasadas</th>{editable && <th />}</tr></thead>
          <tbody>
            {semana.map((d) => {
              const del = (plan?.puntos ?? []).filter((p) => p.fecha === d);
              const puedeDia = editable && d >= hoy;
              return (
                <tr key={d} className={d < hoy ? `lb-inactivo` : ``}>
                  <td className={d === hoy ? `lb-hoy` : ``}><strong>{diaCorto(d)}</strong></td>
                  <td className="lb-celda">{del.length ? del.map((p) => <span key={p.puntoId} className="lb-chip" title={p.nombre}>{p.codigo} × {p.pasadas}</span>) : <span className="lb-sub">Sin programar</span>}</td>
                  <td>{sumaPasadas(del) || `—`}</td>
                  {editable && <td>{puedeDia && <button className="lb-filtro" onClick={() => onEditar(d)}>{del.length ? `Cambiar` : `Programar`}</button>}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="lb-sub">Total de la semana: {total} pasadas.</p>
    </>
  );
}

/** Todos los planes de la semana: lo que valida el coordinador. Marca los puntos que dos supervisores programaron el mismo día. */
function Consolidado({ planes, supervisores, semana, hoy, coordinador, onRevisar, onValidar }) {
  const cruces = new Map();
  for (const p of planes) for (const x of p.puntos) {
    const k = `${x.fecha}|${x.puntoId}`;
    cruces.set(k, (cruces.get(k) ?? 0) + 1);
  }
  const nCruces = [...cruces.values()].filter((n) => n > 1).length;
  const sinPlan = supervisores.filter((s) => !planes.some((p) => p.supervisorId === s.id && p.puntos.length));
  const cuenta = (e) => planes.filter((p) => p.estado === e).length;
  return (
    <>
      <div className="lb-resumen">
        <Cifra valor={cuenta(`ENVIADO`)} etiqueta="planes por validar" alerta={coordinador && cuenta(`ENVIADO`) > 0} />
        <Cifra valor={cuenta(`VALIDADO`)} etiqueta="validados" />
        <Cifra valor={cuenta(`BORRADOR`) + cuenta(`OBSERVADO`)} etiqueta="en preparación o con observación" />
        <Cifra valor={sumaPasadas(planes.flatMap((p) => p.puntos))} etiqueta="pasadas programadas en la semana" />
        {nCruces > 0 && <Cifra valor={nCruces} etiqueta="puntos programados por dos supervisores el mismo día" alerta />}
      </div>
      {sinPlan.length > 0 && <p className="lb-sub">Sin plan todavía: {sinPlan.map((s) => s.nombre).join(`, `)}.</p>}
      {planes.length === 0 && <p className="lb-vacio">Ningún supervisor ha programado esta semana.</p>}
      {planes.map((p) => {
        const filas = [...new Map(p.puntos.map((x) => [x.puntoId, x])).values()].sort((a, b) => a.codigo.localeCompare(b.codigo));
        const est = ESTADO_PLAN[p.estado];
        const vigente = semana[6] >= hoy;
        return (
          <section key={p.id} className="lb-tarjeta">
            <header className="lb-tarjeta-cab">
              <div>
                <h3>{p.supervisor}</h3>
                <p className="lb-sub">
                  <span className={est.clase}>{est.texto}</span>
                  {p.enviadoEn && ` · enviado ${new Date(p.enviadoEn).toLocaleString(`es-PE`, { weekday: `short`, day: `numeric`, hour: `2-digit`, minute: `2-digit` })}`}
                  {p.revisadoPor && (p.estado === `VALIDADO` || p.estado === `OBSERVADO`) && ` · revisó ${p.revisadoPor}`}
                </p>
                {p.estado === `OBSERVADO` && <p className="lb-sub">Observación: {p.observacion}</p>}
              </div>
              {coordinador && vigente && (
                <div className="lb-tarjeta-der">
                  {(p.estado === `ENVIADO` || p.estado === `VALIDADO`) && <button className={claseSecundario} onClick={() => onRevisar(p)}>Devolver con observación</button>}
                  {p.estado === `ENVIADO` && <button className={clasePrimario} onClick={() => onValidar(p)}>Validar</button>}
                </div>
              )}
            </header>
            <div className="lb-tabla-caja">
              <table className="lb-tabla lb-plan">
                <thead><tr><th>Punto</th>{semana.map((d) => <th key={d} className={d === hoy ? `lb-hoy` : ``}>{diaCorto(d)}</th>)}<th>Total</th></tr></thead>
                <tbody>
                  {filas.map((x) => {
                    const fila = semana.map((d) => p.puntos.find((y) => y.puntoId === x.puntoId && y.fecha === d));
                    return (
                      <tr key={x.puntoId}>
                        <td><span className="lb-codigo">{x.codigo}</span> <span className="lb-sub">{x.fundo}</span></td>
                        {fila.map((c, i) => (
                          <td key={semana[i]} className={c && cruces.get(`${semana[i]}|${x.puntoId}`) > 1 ? `lb-cruce` : ``}
                            title={c && cruces.get(`${semana[i]}|${x.puntoId}`) > 1 ? `Otro supervisor también programó este punto ese día` : undefined}>
                            {c ? c.pasadas : ``}
                          </td>
                        ))}
                        <td><b>{sumaPasadas(fila.filter(Boolean))}</b></td>
                      </tr>
                    );
                  })}
                  {filas.length === 0 && <tr><td colSpan={9} className="lb-sub">Sin puntos programados.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </>
  );
}

function DevolverPlan({ plan, onClose, onListo }) {
  const [texto, setTexto] = useState(``);
  const [error, setError] = useState(``);
  const devolver = async () => {
    setError(``);
    try {
      await api(`/labores/plan-trabajo/revisar/${plan.id}`, { method: `POST`, body: JSON.stringify({ validar: false, observacion: texto }) });
      onListo();
    } catch (e) {
      setError(e.message);
    }
  };
  return (
    <Modal titulo={`Devolver el plan de ${plan.supervisor}`} onClose={onClose}>
      <div className="lb-form">
        <label>Qué hay que corregir<textarea className={claseEntrada} rows={4} maxLength={500} value={texto} onChange={(e) => setTexto(e.target.value)} /></label>
        <p className="lb-sub">El supervisor lo verá en su programación y en la campana, lo corrige y lo vuelve a enviar.</p>
        {error && <p className="lb-error">{error}</p>}
        <div className="lb-botones">
          <button className={claseSecundario} onClick={onClose}>Cancelar</button>
          <button className={clasePrimario} disabled={texto.trim().length < 3} onClick={devolver}>Devolver</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Registro de evidencias (admin) ──

const REVISAR = {
  HORA_FUTURA: { texto: `Hora posterior a la llegada`, ayuda: `La hora del trabajo es posterior a cuando llegó: el reloj del celular puede estar adelantado.` },
  MUY_TARDE: { texto: `Llegó +3 h después`, ayuda: `Más de 3 horas entre el trabajo y la llegada.` },
  FUERA: { texto: `Fuera del punto`, ayuda: `Más lejos que el radio del punto.` },
  SIN_UBICACION: { texto: `Sin ubicación`, ayuda: `Llegó sin ubicación.` },
};
const minutosTexto = (m) => (Math.abs(m) < 2 ? `al momento` : Math.abs(m) < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${Math.abs(m % 60)} min`);

function RegistroEvidencias({ trabajadores }) {
  const [desde, setDesde] = useState(sumarDias(hoyISO(), -6));
  const [hasta, setHasta] = useState(hoyISO());
  const [trabajador, setTrabajador] = useState(``);
  const [soloRevisar, setSoloRevisar] = useState(false);
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(``);
  const [foto, setFoto] = useState(null);
  useEffect(() => {
    setFilas(null);
    setError(``);
    api(`/labores/registro?desde=${desde}&hasta=${hasta}${trabajador ? `&trabajador=${trabajador}` : ``}${soloRevisar ? `&revisar=true` : ``}`, { sinCache: true })
      .then(setFilas).catch((e) => setError(e.message));
  }, [desde, hasta, trabajador, soloRevisar]);
  const conteo = Object.fromEntries(Object.keys(REVISAR).map((k) => [k, (filas ?? []).filter((f) => f.revisar.includes(k)).length]));
  const fechaHora = (v) => (v ? new Date(v).toLocaleString(`es-PE`, { day: `2-digit`, month: `short`, hour: `2-digit`, minute: `2-digit` }) : `—`);
  const csv = () => {
    const cab = [`Fecha`, `Trabajador`, `Supervisor`, `Punto`, `Cómo se identificó`, `Fundo`, `Hora del trabajo`, `Llegó`, `Minutos después`, `Distancia (m)`, `Radio (m)`, `Lat`, `Lng`, `Revisar`];
    const lineas = (filas ?? []).map((f) => [f.fecha, f.trabajador, f.supervisor ?? ``, f.codigo ?? ``, f.identificado === `CODIGO` ? `Código escrito` : f.identificado === `UNICO` ? `Único pendiente` : ``, f.fundo ?? ``, f.tomadaEn, f.recibidaEn, f.minutosDespues, f.distanciaM ?? ``, f.radioM, f.lat ?? ``, f.lng ?? ``, f.revisar.map((r) => REVISAR[r].texto).join(` / `)]);
    const texto = [cab, ...lineas].map((l) => l.map((x) => `"${String(x).replace(/"/g, `""`)}"`).join(`,`)).join(`\n`);
    const url = URL.createObjectURL(new Blob([`﻿${texto}`], { type: `text/csv` }));
    const a = document.createElement(`a`);
    a.href = url;
    a.download = `registro-evidencias-${desde}-al-${hasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="lb-asignar">
      <div className="lb-barra-acciones">
        <div className="lb-registro-filtros">
          <label className="lb-fecha"><span>Desde</span><input type="date" className={claseEntrada} value={desde} onChange={(e) => e.target.value && setDesde(e.target.value)} /></label>
          <label className="lb-fecha"><span>Hasta</span><input type="date" className={claseEntrada} value={hasta} onChange={(e) => e.target.value && setHasta(e.target.value)} /></label>
          <label className="lb-fecha"><span>Trabajador</span>
            <select className={claseEntrada} value={trabajador} onChange={(e) => setTrabajador(e.target.value)}>
              <option value="">Todos</option>
              {trabajadores.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </label>
          <label className="lb-check"><input type="checkbox" checked={soloRevisar} onChange={(e) => setSoloRevisar(e.target.checked)} /> Sólo las que hay que revisar</label>
        </div>
        <button className={claseSecundario} onClick={csv} disabled={!filas?.length}>Descargar CSV</button>
      </div>
      <p className="lb-sub">Cada evidencia con la hora en que se hizo (la del mensaje de WhatsApp) y la hora en que llegó. La diferencia muestra cuánto tardó por falta de señal.</p>
      <div className="lb-resumen">
        <div className="lb-cifra"><span className="lb-cifra-valor">{filas?.length ?? `…`}</span><span className="lb-cifra-etiqueta">evidencias</span></div>
        {Object.entries(REVISAR).map(([k, v]) => (
          <div key={k} className={conteo[k] ? `lb-cifra lb-cifra-alerta` : `lb-cifra`} title={v.ayuda}><span className="lb-cifra-valor">{conteo[k]}</span><span className="lb-cifra-etiqueta">{v.texto}</span></div>
        ))}
      </div>
      {error && <p className="lb-error">{error}</p>}
      <div className="lb-tabla-caja">
        <table className="lb-tabla">
          <thead><tr><th>Trabajador</th><th>Punto</th><th>Hora del trabajo</th><th>Llegó</th><th>Diferencia</th><th>Distancia</th><th>Revisar</th><th>Foto</th></tr></thead>
          <tbody>
            {(filas ?? []).map((f) => (
              <tr key={f.id} className={f.revisar.length ? `lb-fila-revisar` : ``}>
                <td>{f.trabajador}<span className="lb-sub"><br />{f.supervisor ?? `—`}</span></td>
                <td><span className="lb-codigo">{f.codigo ?? `—`}</span><span className="lb-sub"><br />{f.fundo ?? ``}{f.identificado === `CODIGO` ? ` · código escrito` : f.identificado === `UNICO` ? ` · sin código` : ``}</span></td>
                <td>{fechaHora(f.tomadaEn)}</td>
                <td>{fechaHora(f.recibidaEn)}</td>
                <td className={f.minutosDespues > 15 ? `lb-aviso` : ``}>{minutosTexto(f.minutosDespues)}</td>
                <td className={f.revisar.includes(`FUERA`) ? `lb-mal` : ``}>{f.distanciaM == null ? `—` : `${f.distanciaM} m`}</td>
                <td>{f.revisar.map((r) => <span key={r} className="lb-chip lb-chip-revisar" title={REVISAR[r].ayuda}>{REVISAR[r].texto}</span>)}</td>
                <td>{f.tieneFoto ? <button className="lb-filtro" onClick={() => setFoto(f)}>Ver</button> : `—`}</td>
              </tr>
            ))}
            {filas && filas.length === 0 && <tr><td colSpan={8} className="lb-sub">No hay evidencias en ese período.</td></tr>}
            {!filas && !error && <tr><td colSpan={8} className="lb-sub">Cargando…</td></tr>}
          </tbody>
        </table>
      </div>
      {foto && (
        <Modal titulo={`${foto.trabajador} · ${foto.codigo ?? ``} · ${fechaHora(foto.tomadaEn)}`} onClose={() => setFoto(null)}>
          <Foto e={{ id: foto.id, tieneFoto: true, tomadaEn: foto.tomadaEn, recibidaEn: foto.recibidaEn, distanciaM: foto.distanciaM, lat: foto.lat, lng: foto.lng, identificado: foto.identificado }} fuera={foto.revisar.includes(`FUERA`)} />
          {foto.lat != null && <p className="lb-sub">Ubicación: {foto.lat.toFixed(5)}, {foto.lng.toFixed(5)}</p>}
        </Modal>
      )}
    </div>
  );
}
